import { makeBidFactory } from "./bids";
import { ICustomizations } from "./providers/types";
import { isMobile, isIosSafari } from "./utils/mobile";
import { BidderMonitor } from "./BidMonitor";
import { createBidstreamMediaSrc, origin } from "./utils/bidstream";
import { 
  renderSafePrebidAd, 
  renderSafeSaambaaAd, 
  renderSafeBidstreamAd, 
  renderSafeApAd, 
  renderSafePubVenturesAd, 
  renderSafeOpscoAd 
} from "./SafeAdRenderers";
import { scrapePageMeta } from './utils/pageMeta';
import { clearContainer } from './utils/dom';
import {
  clearHouseAd,
  fillHouseAd,
  installHouseAdGptListener,
  registerHouseAdSlot,
  setHouseListings,
  HouseAdImpression,
} from './ads/houseAd';
import {
  buildPageTargetingJs,
  parsePageUrlTargeting,
} from './utils/pageUrlTargeting';

export const userLocation = {
  city: {name: ''},
  state: {code: ''},
  country: {code: ''},
  continent: {code: ''}
}

export function appendViewerHeaders (res: Response) {
  const city = res.headers.get('cloudfront-viewer-city')
  if (city) userLocation.city.name = city.toLowerCase()
  const region = res.headers.get('cloudfront-viewer-country-region')
  if (region) userLocation.state.code = region.toUpperCase()
  const country = res.headers.get('cloudfront-viewer-country')
  if (country) userLocation.country.code = country.toUpperCase()

  // Inject geo into Prebid ortb2 config once Prebid processes its queue
  if (country) {
    var geo: any = { country: country.toUpperCase() }
    if (region) geo.region = region.toUpperCase()
    if (city) geo.city = city.toLowerCase()
    var swpbjs = (window as any).swpbjs
    if (swpbjs && swpbjs.mergeConfig) {
      swpbjs.mergeConfig({ ortb2: { user: { geo: geo } } })
    } else {
      // Prebid not loaded yet — queue it
      (window as any).swpbjs = (window as any).swpbjs || {}
      ;((window as any).swpbjs).que = ((window as any).swpbjs).que || []
      ;((window as any).swpbjs).que.push(function () {
        (window as any).swpbjs.mergeConfig({ ortb2: { user: { geo: geo } } })
      })
    }
  }

  return res.json()
}

const gdprCountries = ['AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE','GB']
const ccpaStates = ['CA']

export function isGdprRegion () {
  const code = (userLocation.country.code || '').toUpperCase()
  return gdprCountries.indexOf(code) !== -1
}

export function isCcpaRegion () {
  const code = (userLocation.state.code || '').toUpperCase()
  return ccpaStates.indexOf(code) !== -1
}

export function getEffectiveRefreshMax (theme: ICustomizations): number {
  // iOS Safari/Chrome takes highest priority if explicitly set (Chrome on iOS uses WebKit)
  if (isIosSafari() && theme.adRefreshMaxIosSafari !== undefined && theme.adRefreshMaxIosSafari >= 0) {
    return theme.adRefreshMaxIosSafari
  }
  // Mobile takes next priority if explicitly set
  if (isMobile() && theme.adRefreshMaxMobile !== undefined && theme.adRefreshMaxMobile >= 0) {
    return theme.adRefreshMaxMobile
  }
  // Fall back to default (desktop) value
  return theme.adRefreshMax || 0
}

export function appendScript(root: Element) {
  return function (src: string) {
    const script = document.createElement("script");
    script.src = src;
    root.appendChild(script);
  };
}

export function getRandom() {
  return Math.floor(Math.random() * 1e8);
}

/** Sticky bottom slots are always considered in view (fixed in the viewport). */
function isStickyBottomSlotId(id: string): boolean {
  return id.indexOf('sticky-bottom') !== -1
}

/** Partner placement box when present (e.g. Rtings `.advertisement`). */
function adPlacement(el: Element): Element {
  try {
    return (el.closest && el.closest('.advertisement')) || el
  } catch (e) {
    return el
  }
}

/** True when el or any ancestor has display:none / visibility:hidden. */
function isLayoutHidden(el: Element): boolean {
  let node: Element | null = el
  while (node && node !== document.documentElement) {
    try {
      const cs = window.getComputedStyle(node)
      if (cs.display === 'none' || cs.visibility === 'hidden') return true
    } catch (e) { /* ignore */ }
    node = node.parentElement
  }
  return false
}

/** ≥1px of the placement box intersects the viewport; hidden slots never count. */
export function isAdInView(el: Element): boolean {
  if (isStickyBottomSlotId(el.id)) return true
  const box = adPlacement(el)
  if (isLayoutHidden(box)) return false
  const rect = box.getBoundingClientRect()
  if (rect.width <= 0 || rect.height <= 0) return false
  const vh = window.innerHeight || document.documentElement.clientHeight
  const vw = window.innerWidth || document.documentElement.clientWidth
  return rect.bottom > 0 && rect.right > 0 && rect.top < vh && rect.left < vw
}

/**
 * Fire cb once the placement has ≥1px in view.
 * Rtings keeps extras at display:none — MutationObserver + light poll cover unhide.
 */
export function whenAdInView(el: Element, cb: () => void): void {
  let done = false
  let io: IntersectionObserver | null = null
  let mo: MutationObserver | null = null
  let pollId: ReturnType<typeof setInterval> | null = null
  const box = adPlacement(el)

  const cleanup = () => {
    try { io && io.disconnect() } catch (e) { /* ignore */ }
    try { mo && mo.disconnect() } catch (e) { /* ignore */ }
    if (pollId != null) clearInterval(pollId)
    window.removeEventListener('scroll', check, true)
    window.removeEventListener('resize', check)
  }

  const run = () => {
    if (done) return
    done = true
    cleanup()
    cb()
  }

  const check = () => {
    if (isAdInView(el)) run()
  }

  if (isAdInView(el)) {
    run()
    return
  }

  if (typeof IntersectionObserver !== 'undefined') {
    io = new IntersectionObserver(() => check(), { root: null, rootMargin: '0px', threshold: 0 })
    try { io.observe(box) } catch (e) { /* ignore */ }
    if (box !== el) {
      try { io.observe(el) } catch (e) { /* ignore */ }
    }
  }

  if (typeof MutationObserver !== 'undefined') {
    mo = new MutationObserver(() => check())
    let node: Element | null = box
    for (let i = 0; node && i < 6; i++) {
      mo.observe(node, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] })
      node = node.parentElement
    }
  }

  window.addEventListener('scroll', check, true)
  window.addEventListener('resize', check)
  // Backstop while page is open (hidden slots stay armed until shown)
  pollId = setInterval(check, 500)

  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(check)
}

function injectAdSlotScript(element: Element, src: string | HTMLScriptElement): void {
  if (typeof src === 'string') {
    appendScript(element)(src)
  } else {
    try {
      const s = document.createElement('script')
      s.text = (src as HTMLScriptElement).text || src.textContent || (src as HTMLScriptElement).innerHTML || ''
      element.appendChild(s)
    } catch (e) {
      console.log('Failure to append script', element.id, e)
    }
  }
}


function initIdentityIframe () {
  if (document.getElementById('sellwild-identity-iframe')) return
  var iframe = document.createElement('iframe')
  iframe.id = 'sellwild-identity-iframe'
  iframe.src = 'https://sellwild.com/embed/identity'
  iframe.style.display = 'none'
  iframe.width = '0'
  iframe.height = '0'
  document.body.appendChild(iframe)

  window.addEventListener('message', function (event) {
    if (event.origin !== 'https://sellwild.com') return
    if (!event.data || event.data.type !== 'sellwild-identity') return
    var hem = event.data.hem
    if (!hem) return

    var hemConfig = {
      ortb2: {
        user: {
          data: [{
            name: 'sellwild.com',
            ext: { segtax: 4 },
            segment: [{ id: hem }]
          }],
          eids: [{
            source: 'sellwild.com',
            uids: [{ id: hem, atype: 3 }]
          }]
        }
      }
    }

    var swpbjs = (window as any).swpbjs
    if (swpbjs && swpbjs.mergeConfig) {
      swpbjs.mergeConfig(hemConfig)
    } else if (swpbjs && swpbjs.que) {
      swpbjs.que.push(function () {
        (window as any).swpbjs.mergeConfig(hemConfig)
      })
    }
  })
}

const ihIds = {
  row: '13386',
  ccpa: '13387',
  gdpr: '13388',
}

function getIdentityHubScriptId () {
  if (isGdprRegion()) {
    // console.log("GDPR REGION")
    return ihIds.gdpr
  }

  if (isCcpaRegion()) {
    // console.log("CCPA REGION")
    return ihIds.ccpa
  }

  // console.log("ROW REGION")
  return ihIds.row
}

function getIdentityHubScriptHtml () {
  const id = getIdentityHubScriptId()
  return `(function() {
    var purl = window.location.href;
    var url = '//ads.pubmatic.com/AdServer/js/pwt/160587/${id}';
    var profileVersionId = '';
    if(purl.indexOf('pwtv=')>0){
      var regexp = /pwtv=(.*?)(&|$)/g;
      var matches = regexp.exec(purl);
      if(matches.length >= 2 && matches[1].length > 0){
        profileVersionId = '/'+matches[1];
      }
    }
    var wtads = document.createElement('script');
    wtads.async = true;
    wtads.type = 'text/javascript';
    wtads.src = url+profileVersionId+'/pwt.js';
    var node = document.getElementsByTagName('script')[0];
    node.parentNode.insertBefore(wtads, node);
  })();`
}

function getIdentityHubScript (theme: ICustomizations): HTMLScriptElement | undefined {
  if (theme.identityHub) {
    var exists = document.getElementById('identity-hub-script')
    if (exists) return exists as HTMLScriptElement
    var ih = document.createElement("script")
    ih.id = 'identity-hub-script'
    // TODO SHOULD ALSO CHECK THE GDPR AND CCPA SETTINGS NOT JUST THE REGION

    window.googlefc = window.googlefc || {};
    window.googlefc.callbackQueue = window.googlefc.callbackQueue || [];

    // Queue the callback using the CONSENT_DATA_READY key on the callbackQueue.
    window.googlefc.callbackQueue.push({
      'CONSENT_DATA_READY': (...args: any[]) => console.log('CONSENT_DATA_READY', ...args, window.googlefc.getConsentStatus())
    });

    ih.innerHTML = getIdentityHubScriptHtml()
    return ih
  }

}

interface AuctionBid {
  adUnitCode: string;
  bidder: string;
  cpm: number;
}

interface AuctionEndEvent { 
  bidsReceived: AuctionBid[]
}

const googleTagIndexBySize: { [prefix: string]: {[size: string]: number} } = {}
const adInstanceMap: { [key: string]: ReturnType<typeof generateAds> } = {}

// GAM slot position naming: maps ad sizes to short position abbreviations
const slotSizeAbbrevMap: Record<string, string> = {
  '300x250': 'med',
  '728x90': 'ldr',
  '320x50': 'mldr',
  '160x600': 'sky',
  '300x600': 'half',
  '970x250': 'bill',
}
const slotPositionCounters: { [prefix: string]: Record<string, number> } = {}

function getSlotPosition(size: [number, number], slotPrefix: string, instancePrefix: string): string {
  const sizeStr = `${size[0]}x${size[1]}`
  const abbrev = slotSizeAbbrevMap[sizeStr] || `${size[0]}x${size[1]}`
  // Separate counters for widget (prefixed) vs standalone (unprefixed)
  const key = `${instancePrefix || ''}:${slotPrefix}`
  if (!slotPositionCounters[key]) slotPositionCounters[key] = {}
  slotPositionCounters[key][abbrev] = (slotPositionCounters[key][abbrev] || 0) + 1
  return `${slotPrefix}${abbrev}${slotPositionCounters[key][abbrev]}`
}

/** oRTB 2.5 banner.pos: 3=BTF, 4=Header, 5=Footer. Sticky inferred from div id/label. */
function getBannerPos(tag: string): number {
  if (/sticky-bottom/i.test(tag)) return 5
  if (/sticky/i.test(tag)) return 4
  return 3
}

function normalizeGamPath(tagDesc: string): string {
  return String(tagDesc || '').replace(/^["']|["']$/g, '')
}

/**
 * Impression FPD for Prebid adapters / PBS.
 * GPID is always set (TDD/Index). data.adserver only when GPT/GAM is actually used —
 * PrebidOnly must not claim adserver=gam.
 */
function buildOrtb2Imp(opts: {
  gamPath: string
  slotPosition: string
  includeAdserver: boolean
}): { ext: { gpid: string; data: Record<string, unknown> } } {
  const data: Record<string, unknown> = {
    refreshCount: 0,
  }
  if (opts.includeAdserver && opts.gamPath) {
    data.adserver = { name: 'gam', adslot: opts.gamPath }
  }
  return {
    ext: {
      gpid: opts.gamPath ? `${opts.gamPath}#${opts.slotPosition}` : opts.slotPosition,
      data,
    },
  }
}

// Page-level GAM URL targeting (cached per page; parser from theme.gamUrlParser)
let cachedPageUrlTargeting: { parser: string; kv: Record<string, string> } | null = null
function getCachedPageUrlTargeting(parser: string | undefined): Record<string, string> {
  const id = (parser || 'default').toLowerCase().trim() || 'default'
  if (!cachedPageUrlTargeting || cachedPageUrlTargeting.parser !== id) {
    cachedPageUrlTargeting = {
      parser: id,
      kv: parsePageUrlTargeting(window.location.href, id),
    }
  }
  return cachedPageUrlTargeting.kv
}

export function generateAdsCache(...[theme, ...rest]: Parameters<typeof generateAds>) {
  // Check if we already have an instance for this theme
  const key = theme.slug
  const existingInstance = adInstanceMap[key];
  if (existingInstance) {
    return existingInstance;
  }

  const ads = generateAds(theme, ...rest);
  adInstanceMap[key] = ads;
  return ads;
}

export function generateAds(theme: ICustomizations, { 
  prefix,
  onBidderDone,
  onAuctionEnd,
  onAdRenderSucceeded,
  onDirectRenderSucceeded,
  onHouseAdImpression,
}: { 
  prefix?: string
  onBidderDone?: (event: unknown) => void
  onAuctionEnd?: (winningBids: AuctionBid[]) => void
  onAdRenderSucceeded?: (event: { bid: AuctionBid }) => void
  onDirectRenderSucceeded?: (event: { bid: AuctionBid }) => void
  onHouseAdImpression?: (event: HouseAdImpression) => void
} = {}) {
  const DEFAULT_GAM_NETWORK_CODE = '21824729475'
  const gamNetworkCode = theme.gamNetworkCode || DEFAULT_GAM_NETWORK_CODE
  const gamCurrency = (theme.gamCurrency || 'USD').toUpperCase()
  const usdToCadRate = Number(theme.usdToCadRate) || 0
  const gamCurrencyConfig = gamCurrency !== 'USD' && usdToCadRate > 0
    ? JSON.stringify({
        adServerCurrency: gamCurrency,
        defaultRates: { USD: { [gamCurrency]: usdToCadRate } },
      })
    : 'null'

  googleTagIndexBySize[prefix || ''] = {}
  slotPositionCounters[prefix || ''] = {}
  let appendScripts: [string, string | HTMLScriptElement][] = [];
  let headScripts: HTMLScriptElement[] = [];

  window.swpbjs = window.swpbjs || {};
  const mobile = isMobile();
  const gptBaseUrl = theme.gptProxyUrl || 'https://securepubads.g.doubleclick.net';
  const withBidstream = (zid: number|string, width = 300, height = 250) => {
    if (!zid) return "";
    if (theme.adType === "BidstreamLite") {
      const [tagId, dataZid] = decodeURIComponent(String(zid)).split(':')
      const ad = `<div data-ad-tagid="${tagId}" data-zid="${dataZid}" data-name="SW - ${theme.partnerCode} ${width}x${height}"></div>`
      return ad
    }
    if (theme.adType === "BidstreamNew") {
      const id = `ad-${getRandom()}`;
      const ad = `<div id="${id}"></div>`
      const script = document.createElement("script");
      script.type = 'text/javascript'
      script.innerHTML = `var _avp = _avp || [];
      _avp.push({ tagid: '${id}', alias: '/', type: 'banner', zid: ${zid}, pid: 0, iframe: true, width: ${width}, height: ${height}, refresh: 60, refresh_limit: 1, rmpid: true, secure: true });`

      appendScripts.push([id, script]);
      return ad
    }
    const random = getRandom();
    const id = `friendly_${random}`;
    const ad = `<ins id="${id}"></ins>`;
    const script = createBidstreamMediaSrc(zid, id, random);
    appendScripts.push([id, script]);
    return ad;
  };
  const displayZids = mobile ? theme.mobileZids : theme.displayZids;
  let zids = [...displayZids]
  let adUnits = []
  try {
    adUnits = (0, eval)(theme.adUnits) || []
  } catch (e) {
    console.log('Error evaling adunits')
  }

  // Track which refresh cycle each exclusive item last rendered in,
  // so exclusives run once per refresh (not once per page view)
  const exclusiveLastUsedAtRefresh: { [name: string]: number } = {}

  let mainWaterfall = [{
    name: 'pubventures',
    options: theme.pubVentures,
    renderer: renderPubVenturesAd,
    exclusive: true
  }, {
    name: 'saambaa',
    options: theme.saambaa,
    renderer: renderSaambaaAd,
    exclusive: theme.saambaa?.exclusive ?? false
  }, {
    name: 'opsco',
    options: theme.opsco,
    renderer: renderOpscoAd,
  }, {
    name: 'bidstream',
    options: theme.bidstream,
    renderer: renderBidstreamAd,
  }].filter(w => !w.options?.disabled)

  function renderDirectAd (container: HTMLDivElement, bid: PrebidBid, apBid: ApBid) {
    try {
      const timestamp = Date.now()
      const href = (window.top || window).location.href
      const params = new URLSearchParams(href.split('?')[1] || '')
      const swDecision = params.get('swdecision')
      const percentage = Math.random()
      const size = container.id.split('-')[3]
      const mOrD = mobile ? 'M' : 'D'
      const floorKey = `floor${mOrD}` as 'floorM' | 'floorD'
      const probabilityKey = `probability${mOrD}` as 'probabilityM' | 'probabilityD'
      const placementKey = `placement${mOrD}${size}`

      const waterfall = mainWaterfall
      .filter((w) => {
        if (!w.options || !(placementKey in w.options)) return false
        const placement = w.options[placementKey as keyof typeof w.options]
        if (!placement || (typeof placement === 'string' && placement.trim() === '')) return false
        return true
      })
      .sort((a, b) => {
        return (b.options[floorKey] || 0.1) - (a.options[floorKey] || 0.1)
      })

      for (const item of waterfall) {
        const floor = item.options[floorKey] || 0.1
        const pbBidCpm = Number(bid && bid.cpm) || 0
        const apBidCpm = Number(theme.apstag && theme.apstag[floorKey]) || 0 //Number(apBid && apBid.amznbid) || 0

        if ('disableUntil' in item.options && Number(item.options.disableUntil) > timestamp) {
          continue
        }

        if (apBid && apBid.amzniid && apBidCpm >= pbBidCpm && apBidCpm >= floor) {
          clearHouseAd(container)
          return renderApAd(container, apBid)
        }

        if (bid && pbBidCpm >= floor) {
          clearHouseAd(container)
          return renderPrebidAd(container, bid)
        }

        const placement = item.options[placementKey as keyof typeof item.options]

        if (swDecision && swDecision.toLowerCase() === item.name.toLowerCase()) {
          clearHouseAd(container)
          return item.renderer(container, placement, floor, size)
        }

        if (
          item.options.geo && 
          !(
            item.options.geo.includes(userLocation.continent.code) ||
            item.options.geo.includes(userLocation.country.code) ||
            item.options.geo.includes(userLocation.state.code) ||
            item.options.geo.includes(userLocation.city.name)
          )
        ) {
          continue
        }

        const frequencyStorage = FrequencyCapStorage(`v1_${item.name}_${placementKey}`, item.options)
        const canUse = frequencyStorage.canUse()

        if (!canUse) {
          continue
        }

        // Expected number of ad shows = (Total opportunities × Probability)
        const itemProbability = Number(item.options[probabilityKey as keyof typeof item.options])
        const probability = Number(params.get(item.name)) || percentage
        // HACK FOR DAILY CAPS
        if (!itemProbability || probability < itemProbability) {
          if (item.exclusive && item.name === 'saambaa') {
            const currentRefresh = (window.swpbjs && window.swpbjs.refreshCount) || 0

            // Already rendered Saambaa during this refresh cycle — skip
            if (exclusiveLastUsedAtRefresh[item.name] === currentRefresh) {
              continue
            }

            // Rotate which size slot Saambaa renders in across refreshes
            const validSizeKeys = Object.keys(item.options)
              .filter(k => k.startsWith(`placement${mOrD}`) && item.options[k as keyof typeof item.options]
                && String(item.options[k as keyof typeof item.options]).trim() !== '')
              .sort()

            if (validSizeKeys.length > 1) {
              const targetKey = validSizeKeys[currentRefresh % validSizeKeys.length]
              if (placementKey !== targetKey) {
                continue
              }
            }

            exclusiveLastUsedAtRefresh[item.name] = currentRefresh
          } else if (item.exclusive) {
            // Original behavior for other exclusive items (e.g. PubVentures)
            mainWaterfall = mainWaterfall.filter((w) => w !== item)
          }

          frequencyStorage.used()
          clearHouseAd(container)
          return item.renderer(container, placement, floor, size)
        }
      }
      
      if (bid && (bid.adId || bid.ad)) {
        clearHouseAd(container)
        return renderPrebidAd(container, bid)
      }
    } catch (err) {
      console.log('Error rendering ad', err)
    }
    // PrebidOnly / direct path no-fill → listing house ad (GPT path uses slotRenderEnded).
    fillHouseAd(container, theme, { onImpression: onHouseAdImpression })
  }

  interface PrebidBid {
    cpm: number; 
    width: number; 
    height: number;
    bidder: string; 
    adUnitId: string; 
    adUnitCode: string; 
    currency: string;
    creativeId: string; 
    adId: string; 
    ad: string;
    meta: Record<string, unknown>;
  }

  function renderPrebidAd (container: HTMLDivElement, bid: PrebidBid) {
    if (theme.safeFrame) {
      return renderSafePrebidAd(container, bid)
    }

    if (theme.boltive) {
      return renderProtectedPrebidAd(container, bid)
    }

    const frame = document.createElement('iframe') as HTMLIFrameElement
    frame.scrolling = 'no'
    frame.frameBorder = 'none'
    frame.width = `${bid.width}px`
    frame.height = `${bid.height}px`
    frame.marginHeight = '0'
    frame.marginWidth = '0'
    clearContainer(container)
    container.appendChild(frame)

    if (frame.contentWindow) {
      const doc = frame.contentWindow.document
      window.swpbjs.renderAd(doc, bid.adId)
    }
  }

  function getAdvertiserId (bid: PrebidBid) {
    const defaultValue = 'unknown'
    
    if (bid.meta) {
      return bid.meta.advertiserId || bid.meta.networkName || defaultValue
    }

    return defaultValue
  }

  function createBoltiveTagDetails(bid: PrebidBid) {
    const timeBase36 = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2).split('')
      .map(c => Math.random() < 0.5 ? c.toUpperCase() : c)
      .join('');

    
    return {
      w: bid.width.toString(),
      h: bid.height.toString(),
      adServerDetails: {
        advertiserId: getAdvertiserId(bid),
        campaignId: `${bid.bidder || 'prebid'}_${bid.cpm || 0}_${bid.currency || 'USD'}`,
        creativeId: bid.creativeId || bid.adId,
        lineitemId: bid.adUnitCode || bid.adId,
        adServer: bid.bidder || 'unknown'
      },
      au: bid.adUnitId,
      markupInDom: true,
      currentTagId: `adltag_${timeBase36}_${random}`
    };
  }
  
  function getBoltiveClientId() {
    if (mobile && theme.boltiveClientIdMobile) {
      return theme.boltiveClientIdMobile;
    }
    return theme.boltiveClientId || 'antengo';
  }

  function renderProtectedPrebidAd(container: HTMLDivElement, bid: PrebidBid) {
    // Create iframe as before
    const frame = document.createElement('iframe');
    frame.scrolling = 'no';
    frame.frameBorder = 'none';
    frame.width = `${bid.width}px`;
    frame.height = `${bid.height}px`;
    frame.marginHeight = '0';
    frame.marginWidth = '0';

    // Clear container and append frame
    clearContainer(container);
    container.appendChild(frame);

    if (frame.contentWindow) {
      const doc = frame.contentWindow.document;

      // Initialize Boltive protection
      const random = Math.round(Math.random() * 1e8).toString();
      const clientId = getBoltiveClientId();
      const siteId = `${random}_${clientId}`;
  
      // Load Boltive scripts dynamically
      Promise.resolve()
      .then(() => {
        const script = doc.createElement('script')
        script.innerHTML = `var urlEncodedUrl = "${encodeURIComponent(bid.ad)}";`
        doc.head.appendChild(script)
      })
      .then(() => loadScript(doc, `https://tagan.adlightning.com/${clientId}/blacklist_script.js`))
      .then(() => loadScript(doc, `https://tagan.adlightning.com/${clientId}/blocking_script.js`))
      .then(() => {
        const script = doc.createElement('script')
        script.innerHTML = `
          window["${siteId}"] = {
            tagDetails: ${JSON.stringify(createBoltiveTagDetails(bid))}
          };

          !function(){"use strict";"blocker"in window||(window.blocker=function(e,n,t,o,r){function i(e){var n=window.Request,t=n&&n.prototype||{};if(navigator&&navigator.sendBeacon)navigator.sendBeacon(e);else if(window.fetch){var o={method:"GET"};"keepalive"in t&&(o.keepalive=!0),window.fetch(e,o)}else{var r=new Image(1,1);"loading"in r&&(r.loading="eager"),r.src=e}}if((r=r||{}).bw){if(Math.random()<.1)for(var a="undefined"!=typeof btoa?btoa:String,x="https://${clientId}-tagan.adlightning.com",c=window[e]?window[e].tagDetails:{},d={siteId:"${clientId}",url:window.top.location.href,tagDetails:c,errorData:null,errorMethod:"missing_blocker"},f=function(){var t=(new Date).getTime();"undefined"!=typeof performance&&"function"==typeof performance.now&&(t+=performance.now());return"xxxxxxxxxxxxxxxyxxxxxxxxxxxxxxx".replace(/[xy]/g,function(e){var n=(t+16*Math.random())%16|0;return t=Math.floor(t/16),("x"===e?n:3&n|8).toString(16)})}(),l=encodeURIComponent(JSON.stringify(d)).replace(/%([0-9A-F]{2})/g,function(e,n){return String.fromCharCode("0x"+n)}),g=encodeURIComponent(a(l)),w=8e3-(x.length+100),u=Math.ceil(g.length/w),s=0,p=1;p<=u;p+=1){i(x+"/error?d="+g.substr(s,w)+"&i="+p+"-"+u+"&t="+c.currentTagId+"&r="+f+"&c=${clientId}&z=1"),s+=w}document.write(n)}})}();

          blocker("${siteId}", decodeURIComponent(urlEncodedUrl) + '<!--ADL_WRAPPED-->', false, window, { bw: true });
        `
        doc.head.appendChild(script)

        const cpm = Number(bid.cpm)
        if (onAdRenderSucceeded && !isNaN(bid.cpm)) {
          onAdRenderSucceeded({ bid: { adUnitCode: container.id, bidder: bid.bidder, cpm } })
        }
      });
    }
  }

  interface ApBid {
    slotID: string
    amzniid: string
    amznbid: string
    amznp: string
    amznsz: string
    size: string
  }

  function renderApAd (container: HTMLDivElement, bid: ApBid) {
    if (theme.safeFrame) {
      return renderSafeApAd(container, bid)
    }

    const frame = document.createElement('iframe') as HTMLIFrameElement
    frame.scrolling = 'no'
    frame.frameBorder = 'none'
    frame.width = bid.size.split('x')[0] + 'px'
    frame.height = bid.size.split('x')[1] + 'px'
    frame.marginHeight = '0'
    frame.marginWidth = '0'
    clearContainer(container)
    container.appendChild(frame)

    if (frame.contentWindow) {
      const doc = frame.contentWindow.document
      const script = doc.createElement('script')
      script.innerHTML = `var kvBidObject={kvMap:JSON.parse('%%PATTERN:TARGETINGMAP%%'),url:"%%PATTERN:url%%",bidType:"outstream",cv:"v2.0.0"};function _apstagRenderCallback(a){for(var e=window,n=0;n<5;){if((e=a.ampEnv?window:e.parent).apstag)try{n=5,e.apstag.renderImp(document,"%%PATTERN:amzniid%%",a)}catch(a){}n++}}function _isAmp(){return window.AMP_CONTEXT_DATA&&"AMP-AD"===window.AMP_CONTEXT_DATA.tagName}if(kvBidObject.ampEnv=_isAmp(),kvBidObject.ampEnv){var apstagUrl="//c.amazon-adsystem.com/aax2/apstag.js",s=document.createElement("script");s.onload=function(){_apstagRenderCallback(kvBidObject)},s.src=apstagUrl,window.document.head.appendChild(s)}else _apstagRenderCallback(kvBidObject);`
      .replace(/%%PATTERN:TARGETINGMAP%%/g, JSON.stringify({}))
      .replace(/%%PATTERN:url%%/g, window.location.href)
      // .replace(/%%PATTERN:url%%/g, 'http://localhost:3000')
      .replace(/%%PATTERN:amzniid%%/g, bid.amzniid)
      doc.body.appendChild(script)

      const cpm = Number(bid.amznbid) || 0

      if (onDirectRenderSucceeded && !isNaN(cpm)) {
        onDirectRenderSucceeded({ bid: { adUnitCode: container.id, bidder: 'amazon', cpm } })
      }
    }
  }

  function renderProtectedPubVenturesAd(container: HTMLDivElement, placement: number | string, floor: number, size: string) {
    // Create iframe as before
    const frame = document.createElement('iframe');
    frame.scrolling = 'no';
    frame.frameBorder = 'none';
    frame.width = '100%';
    frame.height = '100%';
    frame.marginHeight = '0';
    frame.marginWidth = '0';

    // Clear container and append frame
    clearContainer(container);
    container.appendChild(frame);

    if (frame.contentWindow) {
      const doc = frame.contentWindow.document;

      // Initialize Boltive protection
      const random = Math.round(Math.random() * 1e8).toString();
      const clientId = getBoltiveClientId();
      const siteId = `${random}_${clientId}`;

      // Create the ad HTML that will be protected
      const adHtml = `<!DOCTYPE html>
          <html>
              <head></head>
              <body>
                <!-- BEGIN JS TAG - ${window.location.host} ${size} < - DO NOT MODIFY -->
                <SCRIPT SRC="https://secure.adnxs.com/ttj?id=${placement}&cb=%5BCACHEBUSTER%5D" TYPE="text/javascript"></SCRIPT>
                <!-- END TAG -->
              </body>
          </html>`;

      // Write the full HTML structure with Boltive protection
      doc.open()

      doc.write(`
          <!DOCTYPE html>
          <html>
              <head>
                <script>var urlEncodedUrl = "${encodeURIComponent(adHtml)}";</script>
                <script src="https://tagan.adlightning.com/${clientId}/blacklist_script.js"></script>
                <script src="https://tagan.adlightning.com/${clientId}/blocking_script.js"></script>
                <script>
                  window["${siteId}"] = {
                    tagDetails: {
                      w: "100%",
                      h: "100%",
                      adServerDetails: {
                        advertiserId: "pubventures",
                        campaignId: "pubventures_${floor}_USD",
                        creativeId: "${placement}",
                        lineitemId: "${container.id}",
                        adServer: "pubventures"
                      },
                      au: "${container.id}",
                      markupInDom: true,
                      currentTagId: "adltag_${Date.now().toString(36)}_${Math.random().toString(36).substring(2)}"
                    }
                  };

                  !function(){"use strict";"blocker"in window||(window.blocker=function(e,n,t,o,r){function i(e){var n=window.Request,t=n&&n.prototype||{};if(navigator&&navigator.sendBeacon)navigator.sendBeacon(e);else if(window.fetch){var o={method:"GET"};"keepalive"in t&&(o.keepalive=!0),window.fetch(e,o)}else{var r=new Image(1,1);"loading"in r&&(r.loading="eager"),r.src=e}}if((r=r||{}).bw){if(Math.random()<.1)for(var a="undefined"!=typeof btoa?btoa:String,x="https://${clientId}-tagan.adlightning.com",c=window[e]?window[e].tagDetails:{},d={siteId:"${clientId}",url:window.top.location.href,tagDetails:c,errorData:null,errorMethod:"missing_blocker"},f=function(){var t=(new Date).getTime();"undefined"!=typeof performance&&"function"==typeof performance.now&&(t+=performance.now());return"xxxxxxxxxxxxxxxyxxxxxxxxxxxxxxx".replace(/[xy]/g,function(e){var n=(t+16*Math.random())%16|0;return t=Math.floor(t/16),("x"===e?n:3&n|8).toString(16)})}(),l=encodeURIComponent(JSON.stringify(d)).replace(/%([0-9A-F]{2})/g,function(e,n){return String.fromCharCode("0x"+n)}),g=encodeURIComponent(a(l)),w=8e3-(x.length+100),u=Math.ceil(g.length/w),s=0,p=1;p<=u;p+=1){i(x+"/error?d="+g.substr(s,w)+"&i="+p+"-"+u+"&t="+c.currentTagId+"&r="+f+"&c=${clientId}&z=1"),s+=w}document.write(n)}})}();

                  blocker("${siteId}", decodeURIComponent(urlEncodedUrl) + '<!--ADL_WRAPPED-->', false, window, { bw: true });
                </script>
              </head>
              <body>
              </body>
          </html>
      `)
      
      doc.close()

      if (onDirectRenderSucceeded) {
        onDirectRenderSucceeded({ bid: { adUnitCode: container.id, bidder: 'pubventures', cpm: floor } })
      }
    }
  }

  function renderProtectedBidstreamAd(container: HTMLDivElement, placement: number | string, floor: number, size: string) {
    // Create iframe for Boltive protection
    const frame = document.createElement('iframe');
    frame.scrolling = 'no';
    frame.frameBorder = 'none';
    frame.width = '100%';
    frame.height = '100%';
    frame.marginHeight = '0';
    frame.marginWidth = '0';

    // Clear container and append frame
    clearContainer(container);
    container.appendChild(frame);

    if (frame.contentWindow) {
      const doc = frame.contentWindow.document;

      // Initialize Boltive protection
      const random = Math.round(Math.random() * 1e8);
      const clientId = getBoltiveClientId();
      const siteId = `${random}_${clientId}`;
      const friendlyId = `friendly_${random}`;

      // Create the Bidstream ad HTML that will be protected
      const bidstreamSrc = `https://ads.bidstreamserver.com/servlet/view/banner/javascript/zone?zid=${placement}&friendly=${friendlyId}&pid=0&fr=60&frlm=1&rmpid=true&random=${random}&encode=1&origin=${encodeURIComponent(origin)}&referrer=${encodeURIComponent(String(document.location))}`;

      const adHtml = `<!DOCTYPE html>
          <html>
              <head></head>
              <body>
                <!-- BEGIN BIDSTREAM TAG -->
                <ins id="${friendlyId}" style="height:100%;background-color:transparent;"></ins>
                <script src="${bidstreamSrc}"></script>
                <!-- END BIDSTREAM TAG -->
              </body>
          </html>`;

      // Write the full HTML structure with Boltive protection
      doc.open();

      doc.write(`
          <!DOCTYPE html>
          <html>
              <head>
                <script>var urlEncodedUrl = "${encodeURIComponent(adHtml)}";</script>
                <script src="https://tagan.adlightning.com/${clientId}/blacklist_script.js"></script>
                <script src="https://tagan.adlightning.com/${clientId}/blocking_script.js"></script>
                <script>
                  window["${siteId}"] = {
                    tagDetails: {
                      w: "100%",
                      h: "100%",
                      adServerDetails: {
                        advertiserId: "bidstream",
                        campaignId: "bidstream_${floor}_USD",
                        creativeId: "${placement}",
                        lineitemId: "${container.id}",
                        adServer: "bidstream"
                      },
                      au: "${container.id}",
                      markupInDom: true,
                      currentTagId: "adltag_${Date.now().toString(36)}_${Math.random().toString(36).substring(2)}"
                    }
                  };

                  !function(){"use strict";"blocker"in window||(window.blocker=function(e,n,t,o,r){function i(e){var n=window.Request,t=n&&n.prototype||{};if(navigator&&navigator.sendBeacon)navigator.sendBeacon(e);else if(window.fetch){var o={method:"GET"};"keepalive"in t&&(o.keepalive=!0),window.fetch(e,o)}else{var r=new Image(1,1);"loading"in r&&(r.loading="eager"),r.src=e}}if((r=r||{}).bw){if(Math.random()<.1)for(var a="undefined"!=typeof btoa?btoa:String,x="https://${clientId}-tagan.adlightning.com",c=window[e]?window[e].tagDetails:{},d={siteId:"${clientId}",url:window.top.location.href,tagDetails:c,errorData:null,errorMethod:"missing_blocker"},f=function(){var t=(new Date).getTime();"undefined"!=typeof performance&&"function"==typeof performance.now&&(t+=performance.now());return"xxxxxxxxxxxxxxxyxxxxxxxxxxxxxxx".replace(/[xy]/g,function(e){var n=(t+16*Math.random())%16|0;return t=Math.floor(t/16),("x"===e?n:3&n|8).toString(16)})}(),l=encodeURIComponent(JSON.stringify(d)).replace(/%([0-9A-F]{2})/g,function(e,n){return String.fromCharCode("0x"+n)}),g=encodeURIComponent(a(l)),w=8e3-(x.length+100),u=Math.ceil(g.length/w),s=0,p=1;p<=u;p+=1){i(x+"/error?d="+g.substr(s,w)+"&i="+p+"-"+u+"&t="+c.currentTagId+"&r="+f+"&c=${clientId}&z=1"),s+=w}document.write(n)}})}();

                  blocker("${siteId}", decodeURIComponent(urlEncodedUrl) + '<!--ADL_WRAPPED-->', false, window, { bw: true });
                </script>
              </head>
              <body>
              </body>
          </html>
      `);

      doc.close();

      if (onDirectRenderSucceeded) {
        onDirectRenderSucceeded({ bid: { adUnitCode: container.id, bidder: 'bidstream', cpm: floor } });
      }
    }
  }

  function renderPubVenturesAd (container: HTMLDivElement, placement: number | string, floor: number, size: string) {
    if (theme.safeFrame && !theme.pubVentures?.disableSafeFrame) {
      return renderSafePubVenturesAd(container, placement, floor, size)
    }

    if (theme.boltive) {
      return renderProtectedPubVenturesAd(container, placement, floor, size)
    }

    const frame = document.createElement('iframe') as HTMLIFrameElement
    frame.scrolling = 'no'
    frame.frameBorder = 'none'
    frame.width = '100%'
    frame.height = '100%'
    frame.marginHeight = '0'
    frame.marginWidth = '0'
    clearContainer(container)
    container.appendChild(frame)

    if (frame.contentWindow) {
      const doc = frame.contentWindow.document
      doc.open()
        
      // Write the full HTML structure
      doc.write(`
          <!DOCTYPE html>
          <html>
              <head></head>
              <body>
                <!-- BEGIN JS TAG - ${window.location.host} ${size} < - DO NOT MODIFY -->
                <SCRIPT SRC="https://secure.adnxs.com/ttj?id=${placement}&cb=%5BCACHEBUSTER%5D" TYPE="text/javascript"></SCRIPT>
                <!-- END TAG -->
              </body>
          </html>
      `)
      
      // Close the document
      doc.close()
    }

    if (onDirectRenderSucceeded) {
      onDirectRenderSucceeded({ bid: { adUnitCode: container.id, bidder: 'pubventures', cpm: floor } })
    }
  }

  function renderProtectedSaambaaAd(container: HTMLDivElement, placement: number | string, floor: number, size: string) {
    // Create iframe for Boltive protection
    const frame = document.createElement('iframe');
    frame.scrolling = 'no';
    frame.frameBorder = 'none';
    frame.width = '100%';
    frame.height = '100%';
    frame.marginHeight = '0';
    frame.marginWidth = '0';

    // Clear container and append frame
    clearContainer(container);
    container.appendChild(frame);

    if (frame.contentWindow) {
      const doc = frame.contentWindow.document;

      // Initialize Boltive protection
      const random = Math.round(Math.random() * 1e8).toString();
      const clientId = getBoltiveClientId();
      const siteId = `${random}_${clientId}`;

      // Create the Saambaa ad HTML that will be protected
      const adHtml = `<!DOCTYPE html>
          <html>
              <head></head>
              <body>
                <!-- BEGIN SAAMBAA WIDGET EMBED ${size} -->
                ${placement}
                <!-- END SAAMBAA WIDGET EMBED -->
              </body>
          </html>`;

      // Write the full HTML structure with Boltive protection
      doc.open();

      doc.write(`
          <!DOCTYPE html>
          <html>
              <head>
                <script>var urlEncodedUrl = "${encodeURIComponent(adHtml)}";</script>
                <script src="https://tagan.adlightning.com/${clientId}/blacklist_script.js"></script>
                <script src="https://tagan.adlightning.com/${clientId}/blocking_script.js"></script>
                <script>
                  window["${siteId}"] = {
                    tagDetails: {
                      w: "100%",
                      h: "100%",
                      adServerDetails: {
                        advertiserId: "saambaa",
                        campaignId: "saambaa_${floor}_USD",
                        creativeId: "${container.id}",
                        lineitemId: "${container.id}",
                        adServer: "saambaa"
                      },
                      au: "${container.id}",
                      markupInDom: true,
                      currentTagId: "adltag_${Date.now().toString(36)}_${Math.random().toString(36).substring(2)}"
                    }
                  };

                  !function(){"use strict";"blocker"in window||(window.blocker=function(e,n,t,o,r){function i(e){var n=window.Request,t=n&&n.prototype||{};if(navigator&&navigator.sendBeacon)navigator.sendBeacon(e);else if(window.fetch){var o={method:"GET"};"keepalive"in t&&(o.keepalive=!0),window.fetch(e,o)}else{var r=new Image(1,1);"loading"in r&&(r.loading="eager"),r.src=e}}if((r=r||{}).bw){if(Math.random()<.1)for(var a="undefined"!=typeof btoa?btoa:String,x="https://${clientId}-tagan.adlightning.com",c=window[e]?window[e].tagDetails:{},d={siteId:"${clientId}",url:window.top.location.href,tagDetails:c,errorData:null,errorMethod:"missing_blocker"},f=function(){var t=(new Date).getTime();"undefined"!=typeof performance&&"function"==typeof performance.now&&(t+=performance.now());return"xxxxxxxxxxxxxxxyxxxxxxxxxxxxxxx".replace(/[xy]/g,function(e){var n=(t+16*Math.random())%16|0;return t=Math.floor(t/16),("x"===e?n:3&n|8).toString(16)})}(),l=encodeURIComponent(JSON.stringify(d)).replace(/%([0-9A-F]{2})/g,function(e,n){return String.fromCharCode("0x"+n)}),g=encodeURIComponent(a(l)),w=8e3-(x.length+100),u=Math.ceil(g.length/w),s=0,p=1;p<=u;p+=1){i(x+"/error?d="+g.substr(s,w)+"&i="+p+"-"+u+"&t="+c.currentTagId+"&r="+f+"&c=${clientId}&z=1"),s+=w}document.write(n)}})}();

                  blocker("${siteId}", decodeURIComponent(urlEncodedUrl) + '<!--ADL_WRAPPED-->', false, window, { bw: true });
                </script>
              </head>
              <body>
              </body>
          </html>
      `);

      doc.close();

      if (onDirectRenderSucceeded) {
        onDirectRenderSucceeded({ bid: { adUnitCode: container.id, bidder: 'saambaa', cpm: floor } });
      }
    }
  }

  function renderSaambaaAd (container: HTMLDivElement, placement: number | string, floor: number, size: string) {
    if (theme.safeFrame && !theme.saambaa?.disableSafeFrame) {
      return renderSafeSaambaaAd(container, placement, floor, size)
    }

    if (theme.boltive) {
      return renderProtectedSaambaaAd(container, placement, floor, size)
    }

    const frame = document.createElement('iframe') as HTMLIFrameElement
    frame.scrolling = 'no'
    frame.frameBorder = 'none'
    frame.width = '100%'
    frame.height = '100%'
    frame.marginHeight = '0'
    frame.marginWidth = '0'
    clearContainer(container)
    container.appendChild(frame)

    if (frame.contentWindow) {
      const doc = frame.contentWindow.document
      doc.open()
        
      // Write the full HTML structure with Saambaa widget code directly from placement
      doc.write(`
          <!DOCTYPE html>
          <html>
              <head></head>
              <body>
                <!-- BEGIN SAAMBAA WIDGET EMBED ${size} -->
                ${placement}
                <!-- END SAAMBAA WIDGET EMBED -->
              </body>
          </html>
      `)
      
      doc.close()
    }

    if (onDirectRenderSucceeded) {
      onDirectRenderSucceeded({ bid: { adUnitCode: container.id, bidder: 'saambaa', cpm: floor } })
    }
  }
  function renderOpscoAd (container: HTMLDivElement, placement: number | string, floor: number, size: string) {
    if (theme.safeFrame) {
      return renderSafeOpscoAd(container, placement, floor, size)
    }

    const frame = document.createElement('iframe') as HTMLIFrameElement
    frame.scrolling = 'no'
    frame.frameBorder = 'none'
    frame.width = '100%'
    frame.height = '100%'
    frame.marginHeight = '0'
    frame.marginWidth = '0'
    clearContainer(container)
    container.appendChild(frame)

    if (frame.contentWindow) {
      const doc = frame.contentWindow.document
      doc.open()
        
      // Write the full HTML structure
      doc.write(`
          <!DOCTYPE html>
          <html>
              <head></head>
              <body>
                <!-- BEGIN OPSCO TAG -->
                <script type="text/javascript" id="${placement}" src="https://tag.ops.co/${placement}/index.js" charset="utf-8" async></script>
                <!-- END OPSCO TAG -->
              </body>
          </html>
      `)
      
      // Close the document
      doc.close()
    }
    
    if (onDirectRenderSucceeded) {
      onDirectRenderSucceeded({ bid: { adUnitCode: container.id, bidder: 'opsco', cpm: floor } })
    }
  }
  function renderBidstreamAd (container: HTMLDivElement, placement: number | string, floor: number, size: string) {
    if (theme.safeFrame && !theme.bidstream?.disableSafeFrame) {
      return renderSafeBidstreamAd(container, placement, floor, size)
    }

    if (theme.boltive) {
      return renderProtectedBidstreamAd(container, placement, floor, size)
    }

    const ins = document.createElement('ins')
    const random = Math.floor(Math.random() * 1e8)
    ins.id = "friendly_" + random;
    ins.style.height = '100%'
    ins.style.backgroundColor = 'transparent'
    const script = document.createElement('script')
    script.src = createBidstreamMediaSrc(placement, 'friendly_' + random, random)
    
    clearContainer(container)
    container.appendChild(ins)
    ins.appendChild(script)

    if (onDirectRenderSucceeded) {
      onDirectRenderSucceeded({ bid: { adUnitCode: container.id, bidder: 'bidstream', cpm: floor } })
    }
  }

  window.swpbjs.renderDirectAd = renderDirectAd
  window.swpbjs.fetchApsTagBids = fetchApsTagBids
  
  const googleUnitsBySize = adUnits.reduce((acc: any, unit: any) => {
    const isMobileUnit = unit.code.toLowerCase().indexOf('mobile') !== -1
    if (mobile && !isMobileUnit) return acc // dont use desktop on mobile
    if (!mobile && isMobileUnit) return acc // dont use mobile on desktop
    const size = unit.mediaTypes.banner.sizes.map((v: number[]) => v.join('x')).join(',')
    if (!acc[size]) acc[size] = []
    acc[size].push(unit)
    return acc
  }, {})
  const makeBids = makeBidFactory(theme)
  const ads = {
    useBidstream: withBidstream,
    setHouseListings,
    useBidstreamDisplay: () => {
      const zid = zids.shift()
      if (!zids.length) zids = [...displayZids]
      return withBidstream(zid || 0)
    },
    useZipRecruiter: (id: number, short = false) => {
      const zipRecruiterUri = 'https://bit.ly/33Rqhv3'
      const zipRecruiterImage = `https://cache.sellwild.com/ziprecruiter/${id}_v2.jpg`
      return `<a href="${zipRecruiterUri}" style="background-image: url(${zipRecruiterImage})" target="_blank" class="sellwild-zip-ad ${short ? "sellwild-listing-short" : ""}" rel="noopener noreferrer">
        <div class="listing-overlay"></div>
      </a>`
    },
    useBids: (size: [number, number]) => makeBids({ mobile, size }),
    useBidstreamSkyscraper: () => withBidstream(theme.skyscraperZid),
    useTopBanner: () => mobile ? withBidstream(theme.mobileBannerZid, 320, 50) : withBidstream(theme.bannerZid, 728, 90),
    useBottomBanner: () => mobile ? withBidstream(theme.mobileBannerZid, 320, 50) : withBidstream(theme.bottomBannerZid, 728, 90),
    useMobileBanner: () => withBidstream(theme.mobileBannerZid, 320, 50),
    useStickyBottomBanner: (options?: { showBranding?: boolean }) => {
      const containerId = `sellwild-sticky-bottom-${getRandom()}`;
      const showBranding = options?.showBranding !== false;

      // Single slot: pick size based on viewport at load time
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 728;
      const adSize: [number, number] = isMobile ? [320, 50] : [728, 90];
      const adContent = ads.useGoogleDisplay(adSize, 'sticky-bottom') ||
        (isMobile
          ? withBidstream(theme.mobileBannerZid, 320, 50)
          : withBidstream(theme.bottomBannerZid || theme.bannerZid, 728, 90));

      const containerHeight = isMobile ? 50 : 90;
      const adWidth = isMobile ? 320 : 728;
      const adHeight = containerHeight;
      const isMinimal = theme.stickyStyle === 'minimal';

      if (isMinimal) {
        const minimalBranding = showBranding && !isMobile ? `
          <span class="sellwild-sticky-branding-prefix" style="color: #999; font-size: 10px; font-weight: 400;">ads by</span>
          <span class="sellwild-sticky-branding-name" style="color: #999; font-weight: 400; font-size: 12px; letter-spacing: 1px; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;">antengo</span>
        ` : '';

        return `
          <div id="${containerId}" class="sellwild-sticky-bottom-container sellwild-sticky-minimal" style="
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 999999;
            display: flex;
            align-items: center;
            justify-content: center;
            height: ${containerHeight}px;
            background: rgba(255, 255, 255, 0.63);
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            transform: translate3d(0, 0, 0);
            -webkit-transform: translate3d(0, 0, 0);
          ">
            <style>
              @media (max-width: 727px) {
                #${containerId} .sellwild-sticky-close { position: absolute !important; top: -28px !important; left: 0 !important; width: 28px !important; height: 28px !important; font-size: 16px !important; border-radius: 4px 4px 0 0 !important; background: rgba(255,255,255,0.85) !important; }
              }
            </style>

            <div style="position: relative; display: flex; align-items: center; justify-content: center; width: ${adWidth}px; height: ${adHeight}px;">
              ${adContent}
            </div>

            <div style="position: absolute; right: 16px; display: flex; align-items: center; gap: 6px;">
              ${minimalBranding}
              <button
                class="sellwild-sticky-close"
                onclick="(function(){ var el = document.getElementById('${containerId}'); if(el) el.style.display = 'none'; })()"
                style="
                  background: transparent;
                  border: none;
                  color: #999;
                  cursor: pointer;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 16px;
                  line-height: 1;
                  flex-shrink: 0;
                  padding: 0;
                "
                aria-label="Close advertisement"
              >&times;</button>
            </div>
          </div>
        `;
      }

      const brandingHtml = showBranding && !isMobile ? `
        <div class="sellwild-sticky-branding" style="
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 0 24px;
          height: 100%;
        ">
          <span class="sellwild-sticky-branding-prefix" style="color: #888; font-size: 13px; font-weight: 400;">ads by</span>
          <span class="sellwild-sticky-branding-name" style="color: white; font-weight: 300; font-size: 26px; letter-spacing: 3px; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;">antengo</span>
        </div>
      ` : '';

      return `
        <div id="${containerId}" class="sellwild-sticky-bottom-container" style="
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 999999;
          background: #0d0d0d;
          display: flex;
          align-items: center;
          justify-content: center;
          height: ${containerHeight}px;
          box-shadow: 0 -2px 20px rgba(0, 0, 0, 0.6);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          transform: translate3d(0, 0, 0);
          -webkit-transform: translate3d(0, 0, 0);
        ">
          <div class="sellwild-sticky-overlay" style="
            position: absolute;
            inset: 0;
            background-image: radial-gradient(circle, rgba(0, 220, 220, 0.15) 1px, transparent 1px);
            background-size: 6px 6px;
            pointer-events: none;
          "></div>
          <div class="sellwild-sticky-overlay" style="
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 40px;
            background: linear-gradient(to top, rgba(0, 220, 220, 0.5) 0%, rgba(0, 200, 200, 0.2) 20%, transparent 100%);
            pointer-events: none;
          "></div>
          <div class="sellwild-sticky-overlay" style="
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 2px;
            background: linear-gradient(90deg, transparent 0%, rgba(0, 230, 230, 0.8) 20%, rgba(0, 255, 255, 1) 50%, rgba(0, 230, 230, 0.8) 80%, transparent 100%);
            pointer-events: none;
          "></div>
          <style>
            @media (max-width: 727px) {
              #${containerId} .sellwild-sticky-close { position: absolute !important; top: -28px !important; left: 0 !important; width: 28px !important; height: 28px !important; font-size: 16px !important; margin-left: 0 !important; border-radius: 4px 4px 0 0 !important; }
            }
          </style>

          <div style="position: relative; display: flex; align-items: center; justify-content: center; width: ${adWidth}px; height: ${adHeight}px; -webkit-backface-visibility: hidden; backface-visibility: hidden;">
            ${adContent}
          </div>

          <div style="position: relative; display: flex; align-items: center; height: 100%;">
            ${brandingHtml}
          </div>

          <button
            class="sellwild-sticky-close"
            onclick="(function(){ var el = document.getElementById('${containerId}'); if(el) el.style.display = 'none'; })()"
            style="
              background: rgba(255, 255, 255, 0.1);
              border: 1px solid rgba(255, 255, 255, 0.2);
              color: white;
              width: 36px;
              height: 36px;
              border-radius: 4px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 20px;
              line-height: 1;
              margin-left: 20px;
              transition: background 0.2s;
              flex-shrink: 0;
            "
            onmouseover="this.style.background='rgba(255,255,255,0.2)'"
            onmouseout="this.style.background='rgba(255,255,255,0.1)'"
            aria-label="Close advertisement"
          >×</button>
        </div>
      `;
    },
    initialize: () => {
      installHouseAdGptListener(() => theme, { onImpression: onHouseAdImpression })
      if (theme.gppEnabled) {
        ads.initializeGPP()
      } else if (theme.adsenseClientId) {
        ads.initializeGoogleCMP()
      } else {
        ads.initializeAdStack()
      }
    },
    initializeAdStack: () => {
      switch (theme.adType) {
        case "MediaFuse": return ads.initializeMediaFuse()
        case "BidstreamNew": return ads.initializeBidstreamNew()
        case "BidstreamLite": return ads.initializeBidstreamLite()
        case "GTag": return ads.initializeGoogleTag()
        case "Prebid":
        case "PrebidOnly":
          return ads.initializePrebid()
      }
    },
    initializeGPP: () => {
      if ('__gpp' in window && typeof window.__gpp === 'function') {
        const __gpp = window.__gpp
        __gpp("addEventListener", (data: any, success: boolean) => {
          if (success) {
            console.log("GPP data", data);
          }
          // remove the listener for one time data access
          // __gpp('removeEventListener', () => {}, data.listenerId);
        });
      }

      if ('__tcfapi' in window && typeof window.__tcfapi === 'function') {
        window.swpbjs = window.swpbjs || {};
        window.swpbjs.que = window.swpbjs.que || [];
        window.__tcfapi('addEventListener', theme.tcfVersion, (data, success) => {
          if (!success || !data) {
            console.error("❌ GFC callback", data, success);
            return;
          } else {
            const { showAds } = shouldShowAds(data)
            if (showAds) {
              ads.initializeAdStack();
              ads.appendScripts(document.body);
            }
          }
        });
      } else {
        ads.initializeAdStack()
        ads.appendScripts(document.body)
      }

    },
    initializeGoogleCMP: () => {
      var id = 'google-cmp-script'
      if (document.getElementById(id)) return
      
      var script = document.createElement("script");
      script.id = id
      script.async = true
      script.src = `https://fundingchoicesmessages.google.com/i/${theme.adsenseClientId}?ers=1`
      script.setAttribute('nonce', 'CMP');
      document.head.appendChild(script);
      // headScripts.push(script);

      window.swpbjs = window.swpbjs || {};
      window.swpbjs.que = window.swpbjs.que || [];

      window.googlefc = window.googlefc || {};
      window.googlefc.callbackQueue = window.googlefc.callbackQueue || [];

      window.googletag = window.googletag || {};
      window.googletag.cmd = window.googletag.cmd || [];
      window.googletag.cmd.push(function() {
        window.googletag.pubads().disableInitialLoad();
      })
      
      let cmpResolved = false;
      const resolveAds = () => {
        if (cmpResolved) return;
        cmpResolved = true;
        ads.initializeAdStack();
        ads.appendScripts(document.body);
      }

      script.onload = () => {
        window.googlefc.callbackQueue.push({
          'CONSENT_API_READY': () => {
            if (typeof window.__tcfapi === 'function') {
              window.__tcfapi('addEventListener', theme.tcfVersion, (data, success) => {
                if (!success || !data) {
                  console.error("❌ GFC callback", data, success);
                  return;
                } else {
                  const { showAds } = shouldShowAds(data)
                  if (showAds) {
                    resolveAds();
                  }
                }
              })
            } else {
              // No TCF API means consent is not required for this user/region (e.g. googlefcInactive)
              resolveAds();
            }
          }
        });

        // GFC doesn't fire CONSENT_API_READY when consent isn't applicable (e.g. US traffic).
        // Watch for the googlefcInactive iframe which signals consent is not needed.
        const observer = new MutationObserver(() => {
          if (document.querySelector('iframe[name="googlefcInactive"]')) {
            observer.disconnect();
            resolveAds();
          }
        });
        observer.observe(document.documentElement, { childList: true, subtree: true });

        // If googlefcInactive already exists by the time we check
        if (document.querySelector('iframe[name="googlefcInactive"]')) {
          observer.disconnect();
          resolveAds();
        }
      }

      script.onerror = () => {
        console.error("❌ Google CMP script failed to load");
        resolveAds();
      }

      if (window.location.href.includes('localhost')) {
        console.log('Localhost detected, initializing ads')
        resolveAds();
      }
    },
    initializeBidstreamNew: () => {
      var id = 'bidstream-new-script'
      if (document.getElementById(id)) return

      var script = document.createElement("script");
      script.id = id
      script.innerHTML = `var _avp = _avp || [];
      (function() {
        var s = document.createElement('script');
        s.type = 'text/javascript'; s.async = true; s.src = 'https://ads.bidstreamserver.com/js/libcode3.js';
        var x = document.getElementsByTagName('script')[0];
        x.parentNode.insertBefore(s, x);
      })();`
      headScripts.push(script);
    },
    initializeGrowthCode: ({ uid, tcf }: { uid?: string; tcf?: string }) => {
      var id = 'growthcode-script'
      if (document.getElementById(id)) return

      var script = document.createElement("script");
      script.id = id
      script.type = 'text/javascript'
      script.innerHTML = theme.growthcode === 'true' || theme.growthcode === '5311W11D' ? `
var _gcio = window._gcio = window._gcio || []; 
var pid = "5311W11D"; 
var uid = ${JSON.stringify(uid || '')}; // Optional
var tcf = ${JSON.stringify(tcf || '')}; // Optional
(function() { 
  var u="//s.cdn.gcprivacy.net/"; 
  _gcio.push(['pid', pid]); 
  _gcio.push(['uid', uid]);        // Optional
  _gcio.push(['tcf', tcf]);        // Optional
  var d=document, g=d.createElement('script'), 
    s=d.getElementsByTagName('script')[0]; 
    g.type='text/javascript'; g.async=true; g.src=u+'5311W11D.min.js'; 
  s.parentNode.insertBefore(g,s); 
})(); 
window.addEventListener('growthCodeEIDPresentEvent', function (event) {
  if (window.swpbjs) {
    swpbjs.que.push(function () {
      swpbjs.mergeConfig(event.detail);
      swpbjs.refreshUserIds();
    })
  }
})` : theme.growthcode ? `
var _gcio = window._gcio = window._gcio || []; 
var pid = "${theme.growthcode}"; 
var uid = ${JSON.stringify(uid || '')}; // Optional
var tcf = ${JSON.stringify(tcf || '')}; // Optional
var nl = true; 
(function() { 
var u="//s.cdn.gcprivacy.net/"; 
_gcio.push(['pid', pid]); 
_gcio.push(['uid', uid]);        // Optional
_gcio.push(['tcf', tcf]);        // Optional
var d=document, g=d.createElement('script'), 
s=d.getElementsByTagName('script')[0]; 
g.type='text/javascript'; g.async=true; g.src=u+'gcid_s.min.js'; s.parentNode.insertBefore(g,s); 
})(); 
` : ''
      headScripts.push(script);
    },
    initializeBidstreamLite: () => {
      var id = 'bidstream-lite-script'
      if (document.getElementById(id)) return

      var script = document.createElement("script");
      script.id = id
      script.innerHTML = `var _hbopts = { alias: '/', type: 'banner', pid: 0 };
      var _hbwrap = _hbwrap || [];
      (function() {
        var hbldr = function (url, resolution, cachebuster, millis, referrer) {
          var s = document.createElement('script'); s.type = 'text/javascript';
          s.async = true; s.src = 'https://' + url + '&resolution=' + resolution +
            '&random=' + cachebuster + '&millis=' + millis + '&referrer=' + referrer;
          var x = document.getElementsByTagName('script')[0];
          x.parentNode.insertBefore(s, x);
        };
        hbldr(
          'ads.bidstreamserver.com/servlet/hbwrap?stack=${theme.bidstreamStack}&pid=0',
          (window.innerWidth||screen.width)+'x'+(window.innerHeight||screen.height),
          Math.floor(89999999*Math.random()+10000000), new Date().getTime(), encodeURIComponent(
            (window!=top&&window.location.ancestorOrigins)?
              window.location.ancestorOrigins[window.location.ancestorOrigins.length-1]:document.location
          )
        );
      })();`
      headScripts.push(script);
    },
    initializeMediaFuse: () => {
      var id = 'media-fuse-script'
      if (document.getElementById(id)) return

      var gpt = document.createElement("script");
      gpt.src = `${gptBaseUrl}/tag/js/gpt.js`;
      gpt.async = true;
      headScripts.push(gpt);

      var mediaFuse = document.createElement("script");
      mediaFuse.id = id
      mediaFuse.innerHTML = `!(function () { window.googletag = window.googletag || {}; window.vmpbjs = window.vmpbjs || {}; window.vpb = window.vpb || {}; vpb.fastLoad = true; googletag.cmd = googletag.cmd || []; vmpbjs.cmd = vmpbjs.cmd || []; var cmds = []; try{ cmds = googletag.cmd.slice(0); googletag.cmd.length = 0; }catch(e){} var ready = false; function exec(cb) { return cb.call(googletag); } var overriden = false; googletag.cmd.push(function () { overriden = true; googletag.cmd.unshift = function (cb) { if (ready) { return exec(cb); } cmds.unshift(cb); if (cb._startgpt) { ready = true; for (var k = 0; k < cmds.length; k++) { exec(cmds[k]); } } }; googletag.cmd.push = function (cb) { if (ready) { return exec(cb); } cmds.push(cb); }; }); if(!overriden){ googletag.cmd.push = function (cb) { cmds.push(cb); }; googletag.cmd.unshift = function (cb) { cmds.unshift(cb); if (cb._startgpt) { ready = true; if (googletag.apiReady) { cmds.forEach(function (cb) { googletag.cmd.push(cb); }); } else { googletag.cmd = cmds; } } }; } var dayMs = 36e5, cb = parseInt(Date.now() / dayMs), vpbSrc = '//player.ads.sellwild.com/prebidlink/' + cb + '/${theme.mediaFuseWrapper}', pbSrc = vpbSrc.replace('wrapper_hb', 'hb'), gptSrc = '${gptBaseUrl}/tag/js/gpt.js', c = document.head || document.body || document.documentElement; function loadScript(src, cb) { var s = document.createElement('script'); s.src = src; s.defer=false; c.appendChild(s); s.onload = cb; s.onerror = function(){ var fn = function(){}; fn._startgpt = true; googletag.cmd.unshift(fn); }; return s; } loadScript(pbSrc); loadScript(gptSrc); loadScript(vpbSrc); })()`
      headScripts.push(mediaFuse);
    },
    initializeGoogleTag: () => {
      var gpt = document.createElement("script");
      gpt.src = `${gptBaseUrl}/tag/js/gpt.js`;
      gpt.async = true;

      headScripts.push(gpt);
    },
    initializePrebid: () => {
      const id = "sellwild-prebid-init"
      const initialized = document.getElementById(id)
      if (!initialized) {
        var gpt = document.createElement("script");
        gpt.src = `${gptBaseUrl}/tag/js/gpt.js`;
        gpt.async = true;

        var prebid = document.createElement("script");
        var defaultSrc = `https://cache.sellwild.com/prebid/${theme.adsenseClientId ? 'main_10_23_0_1_tcf.js' : 'main_10_23_0_1.js'}`
        prebid.src = theme.prebidSrc || defaultSrc;
        // prebid.src = "https://cache.sellwild.com/prebid/display_7_42_0.js";
        // prebid.src = "https://cache.sellwild.com/prebid7_42_0.js";
        prebid.id = id;
        prebid.async = true;

        var googletag = document.createElement("script");

        var ortb2 = ortb2Generator(theme)
        var schain = schainGenerator(theme)
        var s2sConfig = s2sConfigGenerator(theme)
        var consentManagement = consentManagementGenerator(theme)
        var apstagPubId = theme.apstag && theme.apstag.pubId

        googletag.innerHTML = `

        var PREBID_TIMEOUT = 1900;
        var FAILSAFE_TIMEOUT = 2500 + ${(theme.prebidDefer * 1000 || 0) + 16};
        
        // ======== DO NOT EDIT BELOW THIS LINE =========== //
        ${theme.adType === 'PrebidOnly' ? '' : `
          var googletag = googletag || {};
        googletag.cmd = googletag.cmd || [];
        googletag.cmd.push(function() {
          googletag.pubads().disableInitialLoad();
          ${buildPageTargetingJs(getCachedPageUrlTargeting(theme.gamUrlParser))}
        });
        `}
        
        var swpbjs = swpbjs || {};
        swpbjs.que = swpbjs.que || [];
        swpbjs.refreshCount = swpbjs.refreshCount || 0;
        swpbjs.displaySlots = [];
        swpbjs.displayAdUnitCodes = [];
        swpbjs.gamSlots = [];
        swpbjs.gamAdUnitCodes = [];
        swpbjs.bidderSettings = swpbjs.bidderSettings || {};
        swpbjs.bidderSettings.ix = {storageAllowed: true};
        swpbjs.bidderSettings.amx = {storageAllowed: true};
        swpbjs.bidderSettings.appnexus = {storageAllowed: true};
        swpbjs.bidderSettings.equativ = {storageAllowed: true};
        swpbjs.bidderSettings.onetag = {storageAllowed: true};
        swpbjs.bidderSettings.insticator = {storageAllowed: true};
        swpbjs.bidderSettings.medianet = {storageAllowed: true};

        ${s2sConfig && s2sConfig[0] && s2sConfig[0].bidders ? s2sConfig[0].bidders.map((bidder: string) => 
        `swpbjs.bidderSettings['${bidder}'] = swpbjs.bidderSettings['${bidder}'] || {}
        swpbjs.bidderSettings['${bidder}'].bidCpmAdjustment = function (cpm) {
          return cpm * 0.9
        }
        `).join('\n') : ''}

        swpbjs.que.push(function() {
          var syncsPerBidder = ${(() => {
            const iosVal = theme.syncsPerBidderIos;
            const defaultVal = theme.syncsPerBidder;
            // iOS Safari/Chrome gets its own limit if configured (>= 0)
            if (isIosSafari() && iosVal >= 0) return iosVal;
            // General override if configured
            if (defaultVal >= 0) return defaultVal;
            // Prebid default (no override)
            return -1;
          })()};
          var userSyncConfig = {
              filterSettings: {
                iframe: {
                  bidders: '*',      // '*' represents all bidders
                  filter: syncsPerBidder === 0 ? 'exclude' : 'include'
                }
              },
              ${theme.identityHub ? '' : `userIds: [
                {
                  name: "sharedId", //"pubCommonId" as a name is supported for backwards compatibility,
                  storage: {
                    name: "_sharedID", // name of the 1st party cookie, _pubcid is supported for backwards compatibility
                    type: "cookie",
                    expires: 30,
                  },
                },
              ],`}
            };
          if (syncsPerBidder >= 0) {
            userSyncConfig.syncsPerBidder = syncsPerBidder;
          }
          const config = {
            userSync: userSyncConfig,
            enableTIDs: true,
            priceGranularity: "medium",
            enableSendAllBids: true,
            bidderTimeout: 3000,
            pageUrl: window.location.href,
            floors: {
              enabled: ${(mobile ? theme.bidstream?.floorM : theme.bidstream?.floorD) > 0}, // Enable if floor > 0
              enforcement: {
                enforceJS: true,      // Enforce floors in Prebid.js
                enforcePBS: false,    // Not using PBS enforcement
                floorDeals: true,     // Apply floors to deals
                bidAdjustment: false  // Don't adjust bids, just enforce
              },
              data: {
                currency: 'USD',
                modelVersion: 'sellwild-s3-v1',
                schema: {
                  fields: ['mediaType']
                },
                values: {
                  // Single floor value for all banner ads from S3 optimization
                  'banner': ${mobile ? (theme.bidstream?.floorM || 0.1) : (theme.bidstream?.floorD || 0.1)}
                }
              },
              skipRate: 0 // Always enforce floors
            }
          }
          var ortb2 = ${JSON.stringify(ortb2)}
          var schain = ${JSON.stringify(schain)}
          var s2sConfig = ${JSON.stringify(s2sConfig)}
          var consentManagement = ${JSON.stringify(consentManagement)}
          if (ortb2) {
            config.ortb2 = ortb2
          }
          if (schain) {
            config.schain = schain
          }
          if (s2sConfig && s2sConfig.length > 0) {
            config.s2sConfig = s2sConfig
          }
          if (consentManagement) {
            config.consentManagement = consentManagement
          }
          var gamCurrencyConfig = ${gamCurrencyConfig}
          if (gamCurrencyConfig) {
            config.currency = gamCurrencyConfig
          }
          if (${theme.minBidCacheTTL} >= 0) {
            config.minBidCacheTTL = ${theme.minBidCacheTTL}
          }
          if (${theme.eventHistoryTTL} >= 0) {
            config.eventHistoryTTL = ${theme.eventHistoryTTL}
          }
          swpbjs.setConfig(config)
        });

        swpbjs.requestDefer = ${(theme.prebidDefer * 1000 || 0) + 16}
        var refreshOnViewOnly = ${theme.refreshOnViewOnly ? 'true' : 'false'};
        var refreshDebug = ${theme.debug ? 'true' : 'false'} || (typeof location !== 'undefined' && /(?:^|[?&])swdebug=1(?:&|$)/.test(location.search));
        function refreshLog() {
          if (!refreshDebug) return;
          console.log.apply(console, arguments);
        }
        swpbjs.firstLoadedAdUnitCodes = swpbjs.firstLoadedAdUnitCodes || {};
        swpbjs._auctionInFlight = false;
        swpbjs._auctionQueued = false;
        swpbjs._auctionQueuedIsRefresh = false;
        function isAdElementInView(el) {
          if (!el) return false;
          if (el.id && el.id.indexOf('sticky-bottom') !== -1) return true;
          var target = el;
          try {
            var wrap = el.closest && el.closest('.advertisement');
            if (wrap) target = wrap;
          } catch (e) {}
          var node = target;
          while (node && node !== document.documentElement) {
            try {
              var cs = window.getComputedStyle(node);
              if (cs.display === 'none' || cs.visibility === 'hidden') return false;
            } catch (e) {}
            node = node.parentElement;
          }
          var rect = target.getBoundingClientRect();
          if (rect.width <= 0 || rect.height <= 0) return false;
          var vh = window.innerHeight || document.documentElement.clientHeight;
          var vw = window.innerWidth || document.documentElement.clientWidth;
          return rect.bottom > 0 && rect.right > 0 &&
            rect.top < vh && rect.left < vw;
        }
        function slotsMatchingCodes(slots, codes) {
          var set = {};
          (codes || []).forEach(function (c) { set[c] = true; });
          return (slots || []).filter(function (slot) {
            try {
              return set[slot.getSlotElementId()];
            } catch (e) {
              return false;
            }
          });
        }
        function filterNotYetFirstLoaded(codes) {
          return (codes || []).filter(function (code) {
            return !swpbjs.firstLoadedAdUnitCodes[code];
          });
        }
        function markFirstLoaded(codes) {
          (codes || []).forEach(function (code) {
            swpbjs.firstLoadedAdUnitCodes[code] = true;
          });
        }
        // Per-unit refresh: each code gets its own dueAt; display only when due + in view.
        swpbjs.refreshUnitState = swpbjs.refreshUnitState || {};
        var refreshMax = ${getEffectiveRefreshMax(theme)};
        var refreshIntervalMs = ${theme.adRefreshInterval || 30000};

        function allRefreshCodes() {
          return (swpbjs.displayAdUnitCodes || []).concat(swpbjs.gamAdUnitCodes || []);
        }
        function refreshUnitSnapshot() {
          var now = Date.now();
          return allRefreshCodes().map(function (code) {
            var st = swpbjs.refreshUnitState[code] || {};
            return {
              code: code,
              remainingMs: st.due ? 0 : (st.dueAt ? Math.max(0, st.dueAt - now) : null),
              due: !!st.due,
              inView: isAdElementInView(document.getElementById(code)),
              firstLoaded: !!swpbjs.firstLoadedAdUnitCodes[code]
            };
          });
        }
        function hasActiveRefreshUnits() {
          return allRefreshCodes().some(function (code) {
            var st = swpbjs.refreshUnitState[code];
            return st && (st.due || st.timerId || st.dueAt);
          });
        }
        function stopRefreshWatcherIfIdle() {
          if (hasActiveRefreshUnits() || !swpbjs.refreshTickId) return;
          clearInterval(swpbjs.refreshTickId);
          swpbjs.refreshTickId = null;
        }
        function collectFlushableDue(codes) {
          return (codes || []).filter(function (code) {
            var st = swpbjs.refreshUnitState[code];
            if (!st || !st.due) return false;
            return !refreshOnViewOnly || isAdElementInView(document.getElementById(code));
          });
        }
        function tryFlushDueUnits(reason) {
          if (swpbjs.refreshCount >= refreshMax) {
            refreshLog('[sellwild refresh] max reached — stop', {
              refreshCount: swpbjs.refreshCount,
              refreshMax: refreshMax
            });
            return false;
          }
          if (swpbjs._auctionInFlight) return false;

          var dueDisplay = collectFlushableDue(swpbjs.displayAdUnitCodes);
          var dueGam = collectFlushableDue(swpbjs.gamAdUnitCodes);
          if (!dueDisplay.length && !dueGam.length) return false;

          dueDisplay.concat(dueGam).forEach(function (code) {
            var st = swpbjs.refreshUnitState[code];
            if (st) st.due = false;
          });

          swpbjs.refreshViewableDisplayCodes = refreshOnViewOnly ? dueDisplay : null;
          swpbjs.refreshViewableGamCodes = refreshOnViewOnly ? dueGam : null;
          refreshLog('[sellwild refresh] firing', {
            reason: reason,
            refreshCountNext: swpbjs.refreshCount + 1,
            dueDisplay: dueDisplay,
            dueGam: dueGam,
            units: refreshDebug ? refreshUnitSnapshot() : undefined
          });
          swpbjs.refreshCount++;
          requestSellwildBids(true);
          return true;
        }
        function ensureRefreshWatcher() {
          if (swpbjs.refreshTickId) return;
          swpbjs.refreshTickId = setInterval(function () {
            if (refreshDebug) {
              refreshLog('[sellwild refresh] tick', {
                refreshCount: swpbjs.refreshCount,
                units: refreshUnitSnapshot()
              });
            }
            tryFlushDueUnits('tick-in-view-check');
            stopRefreshWatcherIfIdle();
          }, 1000);
        }
        function armUnitRefresh(code) {
          if (!code || swpbjs.refreshCount >= refreshMax) return;
          var st = swpbjs.refreshUnitState[code] || {};
          if (st.timerId) clearTimeout(st.timerId);
          st.due = false;
          st.dueAt = Date.now() + refreshIntervalMs;
          st.timerId = setTimeout(function () {
            st.timerId = null;
            st.due = true;
            refreshLog('[sellwild refresh] unit due (interval elapsed — hold until in view)', {
              code: code,
              units: refreshDebug ? refreshUnitSnapshot() : undefined
            });
            ensureRefreshWatcher();
            tryFlushDueUnits('unit-timer');
          }, refreshIntervalMs);
          swpbjs.refreshUnitState[code] = st;
          refreshLog('[sellwild refresh] unit armed', {
            code: code,
            intervalMs: refreshIntervalMs,
            dueAt: st.dueAt,
            units: refreshDebug ? refreshUnitSnapshot() : undefined
          });
        }
        function scheduleSellwildRefresh(codes) {
          if (swpbjs.refreshCount >= refreshMax) {
            refreshLog('[sellwild refresh] max reached — stop', {
              refreshCount: swpbjs.refreshCount,
              refreshMax: refreshMax
            });
            return;
          }
          var list = (codes && codes.length)
            ? codes
            : allRefreshCodes().filter(function (c) { return swpbjs.firstLoadedAdUnitCodes[c]; });
          list.forEach(armUnitRefresh);
          ensureRefreshWatcher();
        }
        function drainAuctionQueue() {
          if (!swpbjs._auctionQueued) return;
          var queuedRefresh = swpbjs._auctionQueuedIsRefresh;
          swpbjs._auctionQueued = false;
          swpbjs._auctionQueuedIsRefresh = false;
          requestSellwildBids(queuedRefresh);
        }
        function requestSellwildBids (isRefresh) {
          clearTimeout(swpbjs.requestTimerId)
          swpbjs.requestTimerId = setTimeout(function () {
            if (swpbjs._auctionInFlight) {
              swpbjs._auctionQueued = true;
              // First-load must not be starved by a concurrent refresh (ORing || true locked late lazy slots as refresh-only).
              if (!isRefresh) {
                swpbjs._auctionQueuedIsRefresh = false;
              } else if (swpbjs._auctionQueuedIsRefresh !== false) {
                swpbjs._auctionQueuedIsRefresh = true;
              }
              return;
            }
            if(window.IHPWT && typeof window.IHPWT.getUserIds == "function"){
              swpbjs.adUnits.forEach(function(adUnit){
                adUnit.bids.forEach(function(bid){
                  bid["userId"] = IHPWT.deepMerge(ihowpbjs.getUserIds(), bid["userId"] || {});
                });
              });
            }
            swpbjs.initAdserverSet = false;
            swpbjs.pbjsBids = null;
            swpbjs.apsBids = null;
            swpbjs._auctionInFlight = true;
            swpbjs._currentRequestIsRefresh = !!isRefresh;
            
            // Refresh: respect viewable filter. First-load: ALWAYS only not-yet-loaded
            // codes — never reuse refreshViewable* (that stranded late lazy slots).
            var displayCodes;
            var gamCodes;
            if (isRefresh) {
              displayCodes = swpbjs.refreshViewableDisplayCodes || swpbjs.displayAdUnitCodes;
              gamCodes = swpbjs.refreshViewableGamCodes || swpbjs.gamAdUnitCodes;
            } else {
              displayCodes = filterNotYetFirstLoaded(swpbjs.displayAdUnitCodes);
              gamCodes = filterNotYetFirstLoaded(swpbjs.gamAdUnitCodes);
            }
            const allAdUnitCodes = displayCodes.concat(gamCodes);
            if (!allAdUnitCodes.length) {
              swpbjs._auctionInFlight = false;
              if (swpbjs._auctionQueued) {
                drainAuctionQueue();
                return;
              }
              // No new units to first-load; keep refresh timer armed if already started
              if (isRefresh) scheduleSellwildRefresh();
              return;
            }
            // Snapshot for render/refresh once bids return
            swpbjs._requestDisplayCodes = displayCodes;
            swpbjs._requestGamCodes = gamCodes;
            // Keep imp.ext.data.refreshCount current for SSPs that price refreshes differently
            try {
              (swpbjs.adUnits || []).forEach(function (unit) {
                if (!unit.ortb2Imp) unit.ortb2Imp = { ext: {} };
                if (!unit.ortb2Imp.ext) unit.ortb2Imp.ext = {};
                if (!unit.ortb2Imp.ext.data) unit.ortb2Imp.ext.data = {};
                unit.ortb2Imp.ext.data.refreshCount = swpbjs.refreshCount || 0;
              });
            } catch (e) {}
            swpbjs.requestBids({
              adUnitCodes: allAdUnitCodes,
              bidsBackHandler: function (bids) {
                swpbjs.pbjsBids = bids
                if (swpbjs.refreshCount === 0 && !swpbjs._opscoStripped) {
                  try {
                    var adUnits = swpbjs.adUnits;
                    var codes = adUnits.map(function(unit) { return unit.code; });
                    var updatedAdUnits = adUnits.map(function(unit) {
                      return Object.assign({}, unit, {
                        bids: unit.bids.filter(function(bid) { return bid.bidder !== 'opsco'; })
                      });
                    });
                    swpbjs.removeAdUnit(codes);
                    swpbjs.addAdUnits(updatedAdUnits);
                    swpbjs._opscoStripped = true;
                  } catch(e) {}
                }
                initSellwildAdserver()
              },
              timeout: PREBID_TIMEOUT
            });

            swpbjs.apstagSlots = swpbjs.adUnits
              .filter(function (u) { return allAdUnitCodes.indexOf(u.code) !== -1; })
              .map(function (u) {
              return { slotID: u.code, slotName: u.name.replace(/['"]/g, ''), sizes: u.mediaTypes.banner.sizes, mediaType: 'display' }
            });

            ${apstagPubId ? `swpbjs.fetchApsTagBids('${apstagPubId}')` : `swpbjs.apsBids = [];`}
          }, swpbjs.requestDefer)
        }
        
        function finishAuctionCycle() {
          swpbjs._auctionInFlight = false;
          drainAuctionQueue();
        }
        function initSellwildAdserver() {
            if (swpbjs.initAdserverSet) return;

            const pbjsBids = swpbjs.pbjsBids
            const apsBids = swpbjs.apsBids

            if (!pbjsBids || !apsBids) return

            swpbjs.initAdserverSet = true;

            // Use the codes this auction actually requested (not the full registry)
            var displayCodes = swpbjs._requestDisplayCodes || swpbjs.refreshViewableDisplayCodes || swpbjs.displayAdUnitCodes;
            var gamCodes = swpbjs._requestGamCodes || swpbjs.refreshViewableGamCodes || swpbjs.gamAdUnitCodes;

            // RTE-04: Route slots to correct renderer based on array membership
            // RND-01/RND-05: Render displayAdUnitCodes via renderDirectAd (Prebid-only behavior)
            if (displayCodes.length > 0) {
              displayCodes.forEach((bidId) => {
                const container = document.getElementById(bidId)
                if (!container) return
                const bid = pbjsBids[bidId] && pbjsBids[bidId].bids && pbjsBids[bidId].bids[0]
                const apBid = apsBids.find((b) => b.slotID === bidId)
                swpbjs.renderDirectAd(container, bid, apBid)
              })
            }

            // RND-02/RND-03/RND-04: Render gamAdUnitCodes via GAM (setTargeting + refresh)
            // Only mark slots GPT actually refreshed; finish cycle after googletag.cmd so
            // defineSlot from a just-injected lazy script is visible before we re-auction.
            var codesToMark = (displayCodes || []).slice();
            var finishAndMaybeReauction = function () {
              markFirstLoaded(codesToMark);
              swpbjs.refreshViewableDisplayCodes = null;
              swpbjs.refreshViewableGamCodes = null;
              swpbjs._requestDisplayCodes = null;
              swpbjs._requestGamCodes = null;
              scheduleSellwildRefresh(codesToMark);
              finishAuctionCycle();
              // After any auction, pick up lazy slots that joined (or GPT-defined) too late.
              // Otherwise a refresh pass can finish with units still never first-loaded.
              var moreDisplay = filterNotYetFirstLoaded(swpbjs.displayAdUnitCodes);
              var moreGam = filterNotYetFirstLoaded(swpbjs.gamAdUnitCodes);
              if (moreDisplay.length || moreGam.length) {
                setTimeout(function () { requestSellwildBids(false); }, 200);
              }
            };

            if (gamCodes.length > 0) {
              googletag.cmd.push(function() {
                if (window.apstag) {
                  window.apstag.setDisplayBids();
                }
                swpbjs.setTargetingForGPTAsync && swpbjs.setTargetingForGPTAsync(gamCodes);
                var slotsToRefresh = slotsMatchingCodes(swpbjs.gamSlots, gamCodes);
                if (slotsToRefresh.length) {
                  googletag.pubads().refresh(slotsToRefresh);
                  slotsToRefresh.forEach(function (slot) {
                    try { codesToMark.push(slot.getSlotElementId()); } catch (e) {}
                  });
                }
                finishAndMaybeReauction();
              });
            } else {
              finishAndMaybeReauction();
            }
        }
        
        // in case PBJS doesn't load
        setTimeout(function() {
          initSellwildAdserver();
          // If failsafe never got bids, clear in-flight so later lazy slots can auction
          if (!swpbjs.initAdserverSet) {
            finishAuctionCycle();
          }
        }, FAILSAFE_TIMEOUT);
        `;

        window.swpbjs = window.swpbjs || {};
        window.swpbjs.que = window.swpbjs.que || [];
        if (onBidderDone || onAuctionEnd) {
          window.swpbjs.que.push(() => {
            if (onAuctionEnd) window.swpbjs.onEvent('auctionEnd', (event: AuctionEndEvent) => {
              if (event && event.bidsReceived) {
                const bidsByAdUnitCode = event.bidsReceived.reduce((acc, bid) => {
                  if (!acc[bid.adUnitCode]) acc[bid.adUnitCode] = []
                  acc[bid.adUnitCode].push(bid)
                  return acc
                }, {} as Record<string, AuctionBid[]>)
                
                const winningBids = Object.keys(bidsByAdUnitCode).map((code) => {
                  return bidsByAdUnitCode[code].sort((a, b) => b.cpm - a.cpm)[0]
                })
                onAuctionEnd(winningBids)
              }
            })
            if (onAdRenderSucceeded) window.swpbjs.onEvent('adRenderSucceeded', onAdRenderSucceeded)
            if (onBidderDone) window.swpbjs.onEvent('bidderDone', onBidderDone)
          })
        }

        if (theme.growthcode) {
          ads.initializeGrowthCode({})
        }
          
        if (theme.maxFailedAuctions) {
          const bidMonitor = new BidderMonitor(theme.maxFailedAuctions)
          window.swpbjs.que.push(() => {
            window.swpbjs.onEvent('auctionEnd', (event: any) => {
              bidMonitor.checkBidders(event)
            })
          })
        }

        const nextScripts: HTMLScriptElement[] = []
        // GPT is needed for both adType and standaloneAdType if not PrebidOnly
        const needsGpt = theme.adType !== 'PrebidOnly' || (theme.standaloneAdType && theme.standaloneAdType !== 'PrebidOnly')
        if (!theme.disableGpt && needsGpt) nextScripts.push(gpt)
        nextScripts.push(prebid)
        nextScripts.push(googletag)
        headScripts.splice(0, 0, ...nextScripts);
      } else if (window.swpbjs) {
        window.swpbjs = window.swpbjs || {};
        window.swpbjs.que = window.swpbjs.que || [];
        window.swpbjs.requestDefer = (theme.prebidDefer * 1000 || 0) + 16
        if (onBidderDone) {
          window.swpbjs.que.push(() => {
            window.swpbjs.onEvent('bidderDone', onBidderDone)
          })
        }
      }
    },
    useGoogleTag: (tagDesc: string, tagSize: [number, number], tag: string, isStandalone: boolean = false) => {
      var tagDescStr = JSON.stringify(tagDesc);
      var tagSizeStr = JSON.stringify(tagSize);
      // Track Sellwild-owned slot ids so house ads never touch publisher GPT slots.
      registerHouseAdSlot(tag)
      if (!document.getElementById(tag)) {
        // REG-01/REG-03: Determine effective ad type based on standalone vs inline
        // Standalone ads use standaloneAdType (fallback to adType), inline ads use adType
        var effectiveAdType = isStandalone
          ? (theme.standaloneAdType || theme.adType || '')
          : (theme.adType || '')
        var isPrebid = effectiveAdType.indexOf('Prebid') === 0
        var isPrebidOnly = effectiveAdType === 'PrebidOnly'
        // REG-01/REG-02/REG-03/REG-04: Route to correct arrays based on standalone flag
        var slotsArray = isPrebidOnly ? 'displaySlots' : 'gamSlots'
        var adUnitCodesArray = isPrebidOnly ? 'displayAdUnitCodes' : 'gamAdUnitCodes'
        var googletag = document.createElement("script");
        var adscript = document.createElement("script");
        var floor = mobile ? theme.bidstream?.floorM : theme.bidstream?.floorD

        // GAM key-value targeting: slot position (slot-level)
        // Widget ads (sellwild-widget) get "w" prefix; standalone ads (sellwild-ad) do not
        var slotPrefix = isStandalone ? '' : (theme.gamSlotPrefix || 'w')
        var slotPosition = getSlotPosition(tagSize, slotPrefix, prefix || '')
        var gamPath = normalizeGamPath(tagDesc)
        var ortb2Imp = buildOrtb2Imp({
          gamPath,
          slotPosition,
          includeAdserver: !isPrebidOnly,
        })
        var bannerPos = getBannerPos(tag)

        // REG-04: Define GPT slots for standalone ads (and display ads when not PrebidOnly)
        // When lazyLoadAds is on, defer defineSlot into the same script as display() so GPT
        // only learns about a slot when it is activated in view. Eager defineSlot + never
        // refresh produces console "Ad unit did not fetch" on offscreen units.
        var defineSlotJs = ''
        if (!isPrebidOnly) {
          defineSlotJs = `
            window.googletag = window.googletag || {cmd: []};
            googletag.cmd.push(function() {
                const slot = googletag.defineSlot(${isPrebid ? tagDescStr : tagDesc}, ${tagSizeStr}, '${tag}')
                .setTargeting('slot', ['${slotPosition}'])
                .addService(googletag.pubads());
                swpbjs.${slotsArray}.push(slot);
                googletag.pubads().enableSingleRequest();
                googletag.enableServices();
            });`
        }

        adscript.innerHTML = `
          ${isPrebidOnly ? '' : (
            (theme.lazyLoadAds ? defineSlotJs : '') + `
          window.googletag.cmd.push(function() {
            window.googletag.display('${tag}');
          });`
          )}

          ${isPrebid ? `
            swpbjs.que.push(function() {
              swpbjs.addAdUnits(${JSON.stringify([{
                code: tag,
                name: tagDescStr,
                ortb2Imp,
                floors: floor ? {
                  currency: 'USD',
                  schema: {
                    fields: ['mediaType']
                  },
                  values: {
                    'banner': floor
                  }
                } : undefined,
                mediaTypes: {
                  banner: {
                    sizes: [tagSize],
                    pos: bannerPos,
                  }
                },
                bids: makeBids({ mobile, size: tagSize })
              }])});
              swpbjs.${adUnitCodesArray}.push("${tag}")
              requestSellwildBids()
            });
          ` : ''}
        `;

        // Eager path: define in head as before. Lazy path: define runs with appendScripts.
        if (!theme.lazyLoadAds && defineSlotJs) {
          googletag.innerHTML = defineSlotJs;
          headScripts.push(googletag);
        }
        appendScripts.push([tag, adscript]);
      }

      return `<!-- ${tagDescStr} --><div id="${tag}" style='width: ${tagSize[0]}px; height: ${tagSize[1]}px; margin: auto;'></div>`;
    },
    useGoogleDisplay: (size: [number, number], label?: string) => {
      const sizeStr = size.join('x')
      if (!googleTagIndexBySize[prefix || ''][sizeStr]) googleTagIndexBySize[prefix || ''][sizeStr] = 0
      const tag = `${prefix || 'google-tag'}-${sizeStr}-${label ? label : googleTagIndexBySize[prefix || ''][sizeStr]}`;
      var isPrebid = (theme.adType || '').indexOf('Prebid') === 0
      if (theme.adType === 'GTag' || isPrebid) {
        googleTagIndexBySize[prefix || ''][sizeStr]++
        const code = `/${gamNetworkCode}/${theme.slug}-${mobile ? 'mobile' : 'desktop'}-${sizeStr}`
        return ads.useGoogleTag(code, size, tag, false)
      }

      if (!googleUnitsBySize[sizeStr] || !googleUnitsBySize[sizeStr][googleTagIndexBySize[prefix || ''][sizeStr]]) return '';
      const item = googleUnitsBySize[sizeStr][googleTagIndexBySize[prefix || ''][sizeStr]]
      googleTagIndexBySize[prefix || ''][sizeStr]++
      return ads.useGoogleTag(`"${item.code}"`, size, tag, false)
    },
    // REG-01/REG-02/REG-04: Standalone display for sellwild-ad tags - routes to gamAdUnitCodes/gamSlots
    useStandaloneDisplay: (size: [number, number], label?: string) => {
      const sizeStr = size.join('x')
      if (!googleTagIndexBySize[prefix || ''][sizeStr]) googleTagIndexBySize[prefix || ''][sizeStr] = 0
      const tag = `${prefix || 'google-tag'}-${sizeStr}-${label ? label : googleTagIndexBySize[prefix || ''][sizeStr]}`;
      // Use standaloneAdType (fallback to adType) to determine if Prebid is involved
      var effectiveAdType = theme.standaloneAdType || theme.adType || ''
      var isPrebid = effectiveAdType.indexOf('Prebid') === 0
      if (effectiveAdType === 'GTag' || isPrebid) {
        googleTagIndexBySize[prefix || ''][sizeStr]++
        const code = `/${gamNetworkCode}/${theme.slug}-${mobile ? 'mobile' : 'desktop'}-${sizeStr}`
        return ads.useGoogleTag(code, size, tag, true)
      }

      if (!googleUnitsBySize[sizeStr] || !googleUnitsBySize[sizeStr][googleTagIndexBySize[prefix || ''][sizeStr]]) return '';
      const item = googleUnitsBySize[sizeStr][googleTagIndexBySize[prefix || ''][sizeStr]]
      googleTagIndexBySize[prefix || ''][sizeStr]++
      return ads.useGoogleTag(`"${item.code}"`, size, tag, true)
    },
    useAudigent: () => {
      if (theme.audigent) {
        var audigentTag = document.createElement('script')
        audigentTag.innerHTML = `(function(w,d,t,u){
            var a=d.createElement(t);a.async=1;a.src=u+"?url="+encodeURIComponent(w.location.href)+"&ref="+encodeURIComponent(d.referrer);
            var s=d.getElementsByTagName(t)[0]; s.parentNode.insertBefore(a,s);
        })(window, document, 'script', 'https://a.ad.gt/api/v1/u/matches/359')`
        headScripts.push(audigentTag);
      }
    },
    removeScripts: (root: Element) => {
      appendScripts.forEach(([id]) => {
        const element = document.getElementById(id)
        if (element && element.parentElement) {
          element.parentElement.remove()
        }
      })
    },
    appendScripts: async (root: Element) => {
      try {
        if (theme.adDisableDisplay) {
          ads.removeScripts(root)
          return
        }

        if (theme.adGeoBlock) {
          const { continents, countries, states, cities } = theme.adGeoBlock
          // const result = await fetch('https://tbd4rmdvjk.execute-api.us-east-1.amazonaws.com/dev/geo/ip').then((res) => res.json()).catch(err => ({}))
          if (continents && userLocation.continent && userLocation.continent.code && continents.toUpperCase().indexOf(userLocation.continent.code.toUpperCase()) !== -1) {
            ads.removeScripts(root)
            return { blocked: true, type: 'continent', value: userLocation.continent.code }
          }
          if (countries && userLocation.country && userLocation.country.code && countries.toUpperCase().indexOf(userLocation.country.code.toUpperCase()) !== -1) {
            ads.removeScripts(root)
            return { blocked: true, type: 'country', value: userLocation.country.code }
          }
          if (states && userLocation.state && userLocation.state.code && states.toUpperCase().indexOf(userLocation.state.code.toUpperCase()) !== -1) {
            ads.removeScripts(root)
            return { blocked: true, type: 'state', value: userLocation.state.code }
          }
          if (cities && userLocation.city && userLocation.city.name && cities.toLowerCase().indexOf(userLocation.city.name.toLowerCase()) !== -1) {
            ads.removeScripts(root)
            return { blocked: true, type: 'city', value: userLocation.city.name }
          }
        }

        if (theme.adGeoBlockRefresh) {
          const { continents, countries, states, cities } = theme.adGeoBlockRefresh
          if (continents && userLocation.continent && userLocation.continent.code && continents.toUpperCase().indexOf(userLocation.continent.code.toUpperCase()) !== -1) {
            if (window.swpbjs) window.swpbjs.refreshCount = Infinity
          }
          if (countries && userLocation.country && userLocation.country.code && countries.toUpperCase().indexOf(userLocation.country.code.toUpperCase()) !== -1) {
            if (window.swpbjs) window.swpbjs.refreshCount = Infinity
          }
          if (states && userLocation.state && userLocation.state.code && states.toUpperCase().indexOf(userLocation.state.code.toUpperCase()) !== -1) {
            if (window.swpbjs) window.swpbjs.refreshCount = Infinity
          }
          if (cities && userLocation.city && userLocation.city.name && cities.toLowerCase().indexOf(userLocation.city.name.toLowerCase()) !== -1) {
            if (window.swpbjs) window.swpbjs.refreshCount = Infinity
          }
        }

        const ih = getIdentityHubScript(theme)
        if (theme.identityEnabled) initIdentityIframe()

        if (ih) headScripts.push(ih) // NEED TO WAIT TO PUSH TIL NOW BECAUSE IT RELIES ON USER LOCATION
        headScripts.forEach((script) => {
          try {
            document.head.appendChild(script);
          } catch (e) {
            console.log(e);
          }
        });

        // Capture pending slot injectors before clearing (lazy load may fire later)
        const pendingSlotScripts = appendScripts.slice()
        headScripts = [];
        appendScripts = [];

        pendingSlotScripts.forEach(([id, src]) => {
          const element = document.getElementById(id)
          if (!element) {
            console.log('Element not found for append', id)
            return
          }
          const activate = () => injectAdSlotScript(element, src)
          if (!theme.lazyLoadAds || isStickyBottomSlotId(id)) {
            activate()
            return
          }
          // Partner-hidden (e.g. Rtings `.advertisement { display:none }`): these never
          // get geometry, so scroll never "reaches" them. Fill while hidden so a later
          // unhide/swap already has a creative. Viewport lazy only for layout-visible slots.
          const placement = adPlacement(element)
          if (isLayoutHidden(placement)) {
            activate()
          } else {
            whenAdInView(element, activate)
          }
        });
      } catch (e) {
        console.log(e);
      }
    },
  };
  return ads;
}

function ortb2Generator (theme: ICustomizations) {
  const site = ortb2SiteGenerator(theme)
  const user = ortb2UserGenerator(theme)
  return {
    site,
    user,
  }
}

function schainGenerator (theme: ICustomizations) {
  if (theme.schainSid) {
    return {
      "validation": "strict",
      "config": {
        "ver":"1.0",
        "complete": 1,
        "nodes": [
          {
            "asi": theme.schainAsi || "sellwild.com",
            "sid": theme.schainSid,
            "hp": 1
          }
        ]
      }
    }
  }
  return undefined
}

function s2sConfigGenerator (theme: ICustomizations) {
  if (theme.s2sConfig) {
    try {
      return (0, eval)(decodeURIComponent(theme.s2sConfig))
    } catch (err) {
      console.log('Error parsing s2sConfig', err)
      return undefined
    }
  }
  return undefined
}

function consentManagementGenerator (theme: ICustomizations) {
  if (theme.consentManagement) {
    try {
      return (0, eval)(decodeURIComponent(theme.consentManagement))
    } catch (err) {
      console.log('Error parsing consentManagement', err)
      return undefined
    }
  }
  return undefined
}

function ortb2SiteGenerator (theme: ICustomizations) {
  var pageMeta = scrapePageMeta()
  const site: Partial<Ortb2Site> & { publisher?: { id: string; name: string; domain: string }; content?: any } = {
    name: theme.partnerCode,
    domain: window.location.host,
    page: window.location.href,
    publisher: {
      id: theme.partnerCode,
      name: theme.partnerCode,
      domain: window.location.host,
    },
  }

  if (document.referrer) site.ref = document.referrer

  const keywordEl = document.head.querySelector('meta[name="keywords"]')
  if (keywordEl) {
    site.keywords = keywordEl.getAttribute('content') || ''
  } else if (pageMeta.keywords) {
    site.keywords = pageMeta.keywords
  }

  // Always send content.url (Amazon APS). Enrich title/language when available.
  var content: any = {
    url: window.location.href,
  }
  if (pageMeta.title) content.title = pageMeta.title
  try {
    var lang = document.documentElement && document.documentElement.lang
    if (lang) content.language = lang
  } catch (e) {}

  if (theme.iabCats) {
    // oRTB requires cat/pagecat/sectioncat as string arrays, but the CMS may
    // ship IAB_CATS as a bare string (e.g. "IAB15"), so coerce before assigning
    // (demand — Omnidex — rejects a scalar where an array is spec'd).
    var cats = Array.isArray(theme.iabCats) ? theme.iabCats : [theme.iabCats]
    site.cat = cats
    site.pagecat = cats
    site.sectioncat = cats

    content.data = [{
      name: 'sellwild.com',
      ext: { segtax: 5, cids: cats },
      segment: cats.map(function (c: string) { return { id: c } })
    }]
  }

  site.content = content

  var extData: any = {}
  if (pageMeta.pageType) extData.pageType = pageMeta.pageType
  if (pageMeta.category) extData.category = pageMeta.category
  if (pageMeta.contentTier) extData.contentTier = pageMeta.contentTier
  if (pageMeta.author) extData.author = pageMeta.author
  if (pageMeta.tags && pageMeta.tags.length) extData.tags = pageMeta.tags
  if (pageMeta.searchQuery) site.search = pageMeta.searchQuery
  if (Object.keys(extData).length) {
    site.ext = { data: extData } as any
  }

  return site
}

function ortb2UserGenerator (theme: ICustomizations) {
  const user: Partial<Ortb2User> = {}
  if (userLocation.country.code) {
    var geo: any = { country: userLocation.country.code }
    if (userLocation.state.code) geo.region = userLocation.state.code
    if (userLocation.city.name) geo.city = userLocation.city.name
    user.geo = geo
  }
  return user
}

interface Ortb2Site {
  name: string
  domain: string
  cat: string[]
  sectioncat: string[]
  pagecat: string[]
  page: string
  ref: string
  keywords: string
  search: string
  content: {
    userrating: string
    data: [
      {
        name: string
        ext: {
          segtax: number
          cids: string[]
        }
        segment: { id: string }[]
      }
    ]
  }
  ext: {
    data: {
      pageType: string
      category: string
    }
  }
}

interface Ortb2User {
  yob: number;
  gender: "m" | "f";
  keywords: string;
  geo: {
    country: string;
    region?: string;
    city?: string;
  };
  data: [
    {
      name: string;
      ext: { segtax: number };
      segment: { id: string }[];
    }
  ];
  ext: {
    data: {
      registered: true;
      interests: string[];
    };
  };
}

interface FrequencyCapState {
  expires: Date;
  count: number;
}

interface FrequencyCapOptions {
  frequencyMax?: number;
  frequencyDuration?: number; // Duration in minutes
}

function FrequencyCapStorage(key: string, options: FrequencyCapOptions) {
  function canUse(): boolean {
    if (!options.frequencyMax || !options.frequencyDuration) return true;

    const frequencyMax = options.frequencyMax || 1;
    const { count } = get();

    if (count && count >= frequencyMax) {
      return false;
    }

    return true;
  }

  function used(): void {
    const state = get();
    const frequencyDuration = options.frequencyDuration || 60;
    
    // Only set new expiration if this is the start of a new period
    const expires = state.count === 0 
      ? new Date(new Date().getTime() + frequencyDuration * 60 * 1000)
      : state.expires;
    
    set({
      count: (state.count || 0) + 1,
      expires
    });
  }

  function get(): FrequencyCapState {
    try {
      const value = localStorage.getItem(key);
      if (!value) return { count: 0, expires: new Date() };

      const item = JSON.parse(value);
      const expires = new Date(item.expires);
      const now = new Date();

      if (expires < now) {
        localStorage.removeItem(key);
        return { count: 0, expires: new Date() };
      }

      return {
        expires,
        count: item.count
      };
    } catch (err) {
      console.error('Error getting frequency cap:', err);
      return { count: 0, expires: new Date() };
    }
  }

  function set(state: FrequencyCapState): void {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch (err) {
      console.error('Error setting frequency cap:', err);
    }
  }

  return {
    used,
    canUse
  };
}

function loadScript (doc: Document, src: string) {
  return new Promise((resolve, reject) => {
    const script = doc.createElement('script');
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    doc.head.appendChild(script);
  });
}

function fetchApsTagBids (pubID: string) { 

  if (pubID) {
    const script = document.createElement('script');
    script.innerHTML = `!function(a9,a,p,s,t,A,g){if(a[a9])return;function q(c,r){a[a9]._Q.push([c,r])}a[a9]={init:function(){q("i",arguments)},fetchBids:function(){q("f",arguments)},setDisplayBids:function(){},targetingKeys:function(){return[]},_Q:[]};A=p.createElement(s);A.async=!0;A.src=t;g=p.getElementsByTagName(s)[0];g.parentNode.insertBefore(A,g)}("apstag",window,document,"script","//c.amazon-adsystem.com/aax2/apstag.js");`
    document.head.appendChild(script);
  }

  const apstag = (window as any).apstag 
  if (apstag) {
    try {
      apstag.init({
        pubID: pubID,  // Primary pubID
        adServer: 'googletag'
      });
    } catch (err) {
      console.log('Apstag', pubID)
    }
    apstag.fetchBids({
      slots: window.swpbjs.apstagSlots,
      customPubID: pubID,
      timeout: 2000
    }, function (bids: unknown) {
      window.swpbjs.apsBids = bids;
      window.apstag.setDisplayBids();
      (window as any).initSellwildAdserver();
    })
  } else {
    window.swpbjs.apsBids = [];
  }
}

interface TCData {
  gdprApplies: boolean;
  tcString: string;
  eventStatus: 'tcloaded' | 'cmpuishown' | 'useractioncomplete';
  purpose: {
    consents: {
      [purposeId: number]: boolean;
    };
  };
  vendor: {
    consents: {
      [vendorId: number]: boolean;
    };
  };
}

// Extend the Window interface to include __tcfapi
declare global {
  interface Window {
    __tcfapi?: (
      command: string,
      version: number,
      callback: (tcData: TCData, success: boolean) => void,
      parameter?: any
    ) => void;
    swpbjs: any;
    googletag: any;
    apstag: any;
    googlefc: any;
    tcfConsentData?: TCData;
  }
}

const validEventStatuses: Record<string, boolean> = {tcloaded: true, useractioncomplete: true};
function shouldShowAds(tcData: any) {
  if (!tcData) return { showAds: false, tcData, reason: "No TCData" };
  if (!tcData.gdprApplies) return { showAds: true, tcData, reason: "Not GDPR" };

  if (!validEventStatuses[tcData.eventStatus]) {
    return { showAds: false, tcData, reason: "Invalid Event Status" };
  }

  const purpose = 'Store and/or access information on a device';
  const showAds = tcData?.purpose?.consents?.[1] ?? false;
  const reason = showAds ? `${purpose} enabled` : `${purpose} disabled`;
  return { showAds, tcData, reason };
}